import React from 'react';
import { StatusType, CellState, CELL_BG_COLORS } from './NewVerificationTab.styles';
import { AccountSection, EvidenceItem as EvidenceItemType, ExpandableTableRow, DataPointType } from './NewVerificationTab.data';
import {
  EMPTY_VALUE,
  NOT_RELEVANT,
  COLUMN_KEYS,
  DATA_POINT_ADD_LIMITS,
  PARTIAL_MATCH_REASON_LABELS,
  ADDITIONAL_BANK_CODE_LABEL,
} from './NewVerificationTab.constants';
import {
  SupplierValidationPayeeWithAccountsVerification,
  SupplierValidationVerificationRecordEvidence,
  VerificationsAndAutoMatch,
  PayeeWithAccountsVerificationV2,
  SupplierValidationRecord,
  SupplierValidationAccountVerificationRecord,
  AccountVerificationRecordV2,
  StoreSupplierValidationPayeeVerificationRecordRequest,
  StoreSupplierValidationAccountVerificationRecordRequest,
  evidenceTypeText,
  EvidenceType,
  BankNameDatabaseEvidenceServerResponse,
  VerificationFlowType,
} from '@mortee/domain/validationSystem';
import {
  VerificationLegalIdentifierRequest,
  markFirstAsPrimary,
} from '@mortee/domain/validationSystemVerificationLegalIdentifiers';
import { getCountryName, COUNTRY_CODES } from '@app/domain/countries';
import COUNTRIES from '@app/domain/countries';
import { LegalEntityIdType } from '@mortee/domain/vaildatedPayeeManagement';
import {
  convertValidatedAccountValidationSupplierValidationLevelToText,
  ValidatedPayeeAccountValidationStatusSupplierValidationVerification,
} from '@app/domain/validatedPayeeAccount';
import { SanctionsScreeningResultStatus } from '@app/domain/validatedPayee';
import { trimToNull } from '@app/utils/stringUtils';
import { isSwiftCodeValid } from '@app/domain/accountDetailsValidators';
import { VALIDATION_PATTERNS } from '@app/domain/uiConsts';

/**
 * Mirrors the old SVM account form's SWIFT validation (swiftCodeWithSuffixValidator + the
 * alphanumeric character rule) as a synchronous helper for the edit dialog. Returns an error
 * message, or null when the value is valid. Empty is always valid — an unset SWIFT is allowed.
 * Order matches the old form: character check first, then full-code check, then the "enter full
 * code" nudge for a valid 8-char code, then the generic invalid message.
 */
export function validateSwift(rawValue: string): string | null {
  const value = rawValue.trim();
  if (!value) {
    return null;
  }
  if (!VALIDATION_PATTERNS.alphanumeric.test(value)) {
    return 'Invalid Character';
  }
  if (isSwiftCodeValid(value, true)) {
    return null;
  }
  if (isSwiftCodeValid(value, false)) {
    return 'Please enter full SWIFT code';
  }
  return 'Invalid swift code';
}

/**
 * Extracts status type from cell content
 */
export const getStatusType = (cellContent: React.ReactNode | StatusType): StatusType => {
  if (typeof cellContent === 'string' && ['success', 'error', 'warning', 'neutral'].includes(cellContent)) {
    return cellContent as StatusType;
  }

  if (typeof cellContent === 'string' && ['Match', 'Mismatch', 'PartialMatch', 'NotRelevant'].includes(cellContent)) {
    return cellContent as StatusType;
  }

  if (!cellContent || typeof cellContent !== 'object') return null;

  if (React.isValidElement(cellContent)) {
    const props = cellContent.props;
    if (props && props.children) {
      const children = React.Children.toArray(props.children);
      for (const child of children) {
        if (React.isValidElement(child)) {
          if (child.props && child.props.type && ['success', 'warning', 'error', 'neutral'].includes(child.props.type)) {
            return child.props.type;
          }
          if (child.props && child.props.children) {
            const nestedChildren = React.Children.toArray(child.props.children);
            for (const nestedChild of nestedChildren) {
              if (React.isValidElement(nestedChild) && nestedChild.props && nestedChild.props.type) {
                if (['success', 'warning', 'error', 'neutral'].includes(nestedChild.props.type)) {
                  return nestedChild.props.type;
                }
              }
            }
          }
        }
      }
    }
  }

  return null;
};

/**
 * Extracts vendor value from a row
 */
export const extractVendorValue = (row: { vendor?: React.ReactNode | EvidenceItemType[] }): string => {
  if (Array.isArray(row.vendor)) {
    return row.vendor.map((item) => item.value).join(', ');
  }
  if (typeof row.vendor === 'string') {
    return row.vendor;
  }
  return String(row.vendor || '');
};

/**
 * Normalizes a row's vendor value into the list of registration preset items the edit dialog should
 * render — one preset button per item. An array vendor (e.g. the bankName row's DB-resolved bank
 * names) yields one item per element, preserving each item's source label. A scalar vendor yields a
 * single item with no source. Empty / whitespace-only values are dropped.
 */
export function buildVendorPresetItems(vendor: React.ReactNode | EvidenceItemType[]): EvidenceItemType[] {
  if (Array.isArray(vendor)) {
    return vendor.filter((item) => !isEmptyValue(String(item.value ?? '').trim()));
  }
  const scalar = typeof vendor === 'string' ? vendor : String(vendor ?? '');
  return !isEmptyValue(scalar.trim()) ? [{ value: scalar }] : [];
}

/**
 * Extracts evidence value from a row
 */
export const extractEvidenceValue = (row: { evidence?: React.ReactNode | EvidenceItemType[] }): string => {
  if (Array.isArray(row.evidence)) {
    return row.evidence.length > 0 ? row.evidence[0].value : '';
  }
  if (typeof row.evidence === 'string') {
    return row.evidence;
  }
  return String(row.evidence || '');
};

/**
 * Converts cell content to string value
 */
export const cellContentToString = (content: React.ReactNode): string => {
  if (typeof content === 'string') {
    return content;
  }
  return String(content || '');
};

/**
 * Determines if a value should be considered empty
 */
export const isEmptyValue = (value: string | undefined): boolean => {
  return !value || value === EMPTY_VALUE || value === NOT_RELEVANT;
};

/**
 * Returns true if manualProcess or finalReview differ between current and initial row
 */
function rowHasManualOrFinalReviewChange(currentRow: ExpandableTableRow, initialRow: ExpandableTableRow): boolean {
  const currentManual = cellContentToString(currentRow.manualProcess);
  const initialManual = cellContentToString(initialRow.manualProcess);
  const currentFinal = cellContentToString(currentRow.finalReview);
  const initialFinal = cellContentToString(initialRow.finalReview);
  return currentManual !== initialManual || currentFinal !== initialFinal;
}

/**
 * Returns true if any row has a change in manualProcess or finalReview
 */
export function payeeRowsHaveManualOrFinalReviewChanges(
  currentRows: ExpandableTableRow[],
  initialRows: ExpandableTableRow[],
): boolean {
  if (currentRows.length !== initialRows.length) return true;
  return currentRows.some((row, i) => initialRows[i] && rowHasManualOrFinalReviewChange(row, initialRows[i]));
}

/**
 * Returns true if any account section has rows with changes in manualProcess or finalReview
 */
export function accountSectionsHaveManualOrFinalReviewChanges(
  currentSections: { id: string; rows: ExpandableTableRow[] }[],
  initialSections: { id: string; rows: ExpandableTableRow[] }[],
): boolean {
  if (currentSections.length !== initialSections.length) return true;
  return currentSections.some(
    (section, i) => initialSections[i] && payeeRowsHaveManualOrFinalReviewChanges(section.rows, initialSections[i].rows),
  );
}

/**
 * Serializes one review column's values (in row order) so two row lists can be compared for changes
 * in THAT column alone. Column-scoped and position-independent of the other column: adding or removing
 * a row only registers as a change for the column that actually carries a value, so a new row typed
 * into Manual Processing is not mis-attributed as a Final Review change (and vice versa). This is what
 * keeps a new LEI from being saved to both flows regardless of the editor's permissions.
 */
function columnValues(rows: ExpandableTableRow[], columnKey: 'manualProcess' | 'finalReview'): string[] {
  return rows.map((row) => cellContentToString(row[columnKey]));
}

function columnValuesDiffer(
  currentRows: ExpandableTableRow[],
  initialRows: ExpandableTableRow[],
  columnKey: 'manualProcess' | 'finalReview',
): boolean {
  const current = columnValues(currentRows, columnKey);
  const initial = columnValues(initialRows, columnKey);
  if (current.length !== initial.length) {
    // A row was added/removed: it's a change for this column only if the added/removed rows carry a
    // value in this column. Compare the non-empty values (order preserved) so an empty new row — or a
    // row that only holds the OTHER column's value — does not register as a change here.
    const currentNonEmpty = current.filter((v) => !isEmptyValue(v));
    const initialNonEmpty = initial.filter((v) => !isEmptyValue(v));
    if (currentNonEmpty.length !== initialNonEmpty.length) return true;
    return currentNonEmpty.some((v, i) => v !== initialNonEmpty[i]);
  }
  return current.some((v, i) => v !== initial[i]);
}

/**
 * Returns true if any payee row has a change in the manualProcess column
 */
export function payeeRowsHaveManualProcessChanges(currentRows: ExpandableTableRow[], initialRows: ExpandableTableRow[]): boolean {
  return columnValuesDiffer(currentRows, initialRows, 'manualProcess');
}

/**
 * Column-scoped change detection across account sections, matched by section id (not index).
 *
 * A section that has no baseline counterpart (freshly added or duplicated) is compared against an
 * empty row list, so it registers a change ONLY for the column that actually carries a value — the
 * same rule columnValuesDiffer applies at the row level. This is what keeps a brand-new account
 * that a Validator filled only in Manual Processing from being mis-attributed as a Final Review
 * change (which previously POSTed a spurious Final-flow record). Matching by id (not position) also
 * means inserting/duplicating a section in the middle does not shift every later section's baseline.
 */
function accountSectionsColumnDiffer(
  currentSections: { id: string; rows: ExpandableTableRow[] }[],
  initialSections: { id: string; rows: ExpandableTableRow[] }[],
  columnKey: 'manualProcess' | 'finalReview',
): boolean {
  const initialById = new Map(initialSections.map((section) => [section.id, section.rows]));
  // A section present in the baseline but removed from current: treat as diffing its rows against
  // an empty list, so a deleted account that carried a value in this column registers a change here.
  const removed = initialSections.filter((section) => !currentSections.some((c) => c.id === section.id));
  return (
    currentSections.some((section) => columnValuesDiffer(section.rows, initialById.get(section.id) ?? [], columnKey)) ||
    removed.some((section) => columnValuesDiffer([], section.rows, columnKey))
  );
}

/**
 * Returns true if any account section has rows with changes in the manualProcess column
 */
export function accountSectionsHaveManualProcessChanges(
  currentSections: { id: string; rows: ExpandableTableRow[] }[],
  initialSections: { id: string; rows: ExpandableTableRow[] }[],
): boolean {
  return accountSectionsColumnDiffer(currentSections, initialSections, 'manualProcess');
}

/**
 * Returns true if any payee row has a change in the finalReview column
 */
export function payeeRowsHaveFinalReviewChanges(currentRows: ExpandableTableRow[], initialRows: ExpandableTableRow[]): boolean {
  return columnValuesDiffer(currentRows, initialRows, 'finalReview');
}

/**
 * Returns true if any account section has rows with changes in the finalReview column
 */
export function accountSectionsHaveFinalReviewChanges(
  currentSections: { id: string; rows: ExpandableTableRow[] }[],
  initialSections: { id: string; rows: ExpandableTableRow[] }[],
): boolean {
  return accountSectionsColumnDiffer(currentSections, initialSections, 'finalReview');
}

/**
 * Returns true if any account section's shared metadata (validationType or privateAccount) changed.
 * This metadata is not carried in a review column — it lives on the section and is written into
 * whichever account request (Manual/Final) gets saved (see buildAllAccountRequests) — so a change
 * to it alone must still enable Save (the caller routes it to the editing user's active flow). The
 * column-scoped row detectors above never see it, which is why changing only the validation type
 * otherwise looked like "no changes".
 */
export function accountSectionsHaveMetadataChanges(
  currentSections: { id: string; validationType: string | undefined; privateAccount: boolean }[],
  initialSections: { id: string; validationType: string | undefined; privateAccount: boolean }[],
): boolean {
  if (currentSections.length !== initialSections.length) return true;
  return currentSections.some(
    (section, i) =>
      initialSections[i] &&
      (section.validationType !== initialSections[i].validationType ||
        section.privateAccount !== initialSections[i].privateAccount),
  );
}

/**
 * Returns true if any row has a non-empty saved value in the given column
 * (manualProcess or finalReview). Empty string, undefined, '-' and 'Not relevant'
 * all count as empty (via isEmptyValue).
 */
export function payeeRowsHaveColumnData(rows: ExpandableTableRow[], columnKey: 'manualProcess' | 'finalReview'): boolean {
  return rows.some((row) => !isEmptyValue(cellContentToString(row[columnKey])));
}

/**
 * Returns true if any account section has a row with a non-empty value in the given column.
 */
export function accountSectionsHaveColumnData(
  sections: { id: string; rows: ExpandableTableRow[] }[],
  columnKey: 'manualProcess' | 'finalReview',
): boolean {
  return sections.some((section) => payeeRowsHaveColumnData(section.rows, columnKey));
}

/**
 * Parses date from DD.MM.YYYY format
 */
export const parseDate = (value: string): number | undefined => {
  const dateMatch = value.match(/(\d{2})\.(\d{2})\.(\d{4})/);
  if (dateMatch) {
    const [, day, month, year] = dateMatch;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (!isNaN(date.getTime())) {
      return date.getTime();
    }
  }
  return undefined;
};

/**
 * Formats date to DD.MM.YYYY format
 */
export const formatDate = (timestamp: number | undefined): string => {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleDateString('en-GB').replace(/\//g, '.');
};

/**
 * Extracts numeric value from amount string
 */
export const extractNumericAmount = (value: string): string => {
  return value.replace(/[^0-9]/g, '');
};

/**
 * Formats amount with currency symbol
 */
export const formatAmount = (amount: string): string => {
  return `$ ${amount}`;
};

/**
 * Gets the base name from a data point (removes trailing numbers)
 */
export const getBaseDataPointName = (dataPoint: string): string => {
  return dataPoint.replace(/\s+\d+$/, '').trim();
};

/**
 * Generates a new data point name with number
 */
export const generateDataPointName = (baseName: string, rowNumber: number): string => {
  return `${baseName} ${rowNumber}`;
};

/**
 * Extracts string value from a potentially wrapped object
 */
function extractStringValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && 'value' in value) {
    const obj = value as { value: unknown; hasChangedFromInitialState?: boolean };
    if (typeof obj.value === 'string') return obj.value;
    if (obj.value === null || obj.value === undefined) return undefined;
    return String(obj.value);
  }
  return String(value);
}

export function formatCountryDisplay(countryCode: string): string {
  const name = getCountryName(countryCode) || COUNTRIES[countryCode]?.name;
  return name ? `${countryCode} - ${name}` : countryCode;
}

/**
 * Resolve a country string to its country CODE, accepting any form the edit dialog handles: the
 * formatted "CODE - Name" display (from a saved cell or a preset button), a plain country name, or a
 * bare code. Inverse of formatCountryDisplay — the edit dialog stores the code and formats on save,
 * so a "CODE - Name" value must be reduced to its code first or it gets formatted again (producing
 * "US - United States - US - United States"). Returns the input unchanged when nothing matches.
 */
export function resolveCountryCode(raw: string | undefined | null): string {
  if (!raw) return '';
  const codeFromFormatted = raw.match(/^([A-Z]{2,3})\s*-\s*.+$/)?.[1];
  return (
    codeFromFormatted ||
    Object.entries(COUNTRIES).find(
      ([code, country]) => country.name === raw || getCountryName(code) === raw || code === raw,
    )?.[0] ||
    raw
  );
}

/**
 * Gets vendor registration value for a data point type from verification data
 */
function getVendorValueForDataPoint(dataPointType: DataPointType, record: SupplierValidationRecord): string | undefined {
  const { supplierRegistrationProcess } = record;
  switch (dataPointType) {
    case 'country': {
      const countryCode = supplierRegistrationProcess?.countryCode;
      return countryCode ? formatCountryDisplay(countryCode) : undefined;
    }
    case 'name':
      return supplierRegistrationProcess?.companyName || undefined;
    case 'address':
      // Prefer the structured formattedAddress; when addressDetails is null (no structured address
      // was parsed) fall back to the raw fullAddress so the vendor address still shows.
      return (
        supplierRegistrationProcess?.address?.addressDetails?.formattedAddress ||
        supplierRegistrationProcess?.address?.fullAddress ||
        undefined
      );
    default:
      return undefined;
  }
}

/**
 * Gets vendor registration value for account data point type
 */
function getAccountVendorValueForDataPoint(
  dataPointType: DataPointType,
  record: SupplierValidationRecord,
  account?: SupplierValidationAccountVerificationRecord | undefined,
): string | undefined {
  const { supplierRegistrationProcess } = record;

  switch (dataPointType) {
    case 'accountCountry': {
      const countryCode = supplierRegistrationProcess?.accountBankCountryCode;
      return countryCode ? formatCountryDisplay(countryCode) : undefined;
    }
    case 'bankName':
      return extractStringValue(account?.bankName);
    case 'bankCode':
      return account?.bankCode
        ? extractStringValue(account?.bankCode)
        : supplierRegistrationProcess?.accountDetails?.bankCode || supplierRegistrationProcess?.abaRouting || undefined;
    case 'branchCode':
      return account?.branchCode
        ? extractStringValue(account?.branchCode)
        : record.supplierRegistrationProcess?.accountDetails?.branchCode || undefined;
    case 'iban':
      return account?.iban
        ? extractStringValue(account?.iban)
        : record.supplierRegistrationProcess?.accountDetails?.iban || undefined;
    case 'swiftCode':
      return account?.swift
        ? extractStringValue(account?.swift)
        : record.supplierRegistrationProcess?.accountDetails?.swiftCode || undefined;
    case 'accountNumber':
      return account?.accountNumber
        ? extractStringValue(account?.accountNumber)
        : record.supplierRegistrationProcess?.accountDetails?.accountNumber || undefined;
    case 'forFurtherCredit':
      // furtherCredit is a top-level field on the registration process (not under accountDetails,
      // unlike bankCode/iban/etc.), so fall back to it when the verification account has no value —
      // this is what surfaces the vendor's Further Credit in the Vendor Registration column.
      return account?.furtherCredit
        ? extractStringValue(account?.furtherCredit)
        : supplierRegistrationProcess?.furtherCredit || undefined;
    default:
      return undefined;
  }
}

/**x
 * Gets evidence value for a data point type
 */
function getEvidenceValueForDataPoint(
  dataPointType: DataPointType,
  evidence: SupplierValidationVerificationRecordEvidence | undefined,
): EvidenceItemType[] | undefined {
  if (!evidence || !evidence.data) return undefined;

  const evidenceItems: EvidenceItemType[] = [];
  const typeLabel = evidence.type ? evidenceTypeText[evidence.type as EvidenceType] : undefined;

  switch (dataPointType) {
    case 'country': {
      const countryCode = evidence.data.payeeCountryCode;
      if (countryCode) {
        evidenceItems.push({ value: formatCountryDisplay(countryCode) });
      }
      break;
    }
    case 'accountCountry': {
      const countryCode = evidence.data.accountCountryCode;
      if (countryCode) {
        evidenceItems.push({ value: formatCountryDisplay(countryCode) });
      }
      break;
    }
    case 'name':
      if (evidence.data.payeeName) {
        evidenceItems.push({ value: evidence.data.payeeName });
      }
      if (evidence.data.formerNames && evidence.data.formerNames.length > 0) {
        evidence.data.formerNames.forEach((name) => evidenceItems.push({ value: name, source: 'Former Name' }));
      }
      if (evidence.data.alternativeNames && evidence.data.alternativeNames.length > 0) {
        evidence.data.alternativeNames.forEach((name) => evidenceItems.push({ value: name, source: 'Alternative Name' }));
      }
      break;
    case 'address':
      if (evidence.data.address) {
        evidenceItems.push({ value: evidence.data.address });
      } else if (evidence.data.formattedAddress) {
        evidenceItems.push({ value: evidence.data.formattedAddress, source: 'Formatted' });
      }
      break;
    case 'bankName':
      if (evidence.data.bankName) {
        evidenceItems.push({ value: evidence.data.bankName });
      }
      break;
    case 'bankCode':
      if (evidence.bankCode) {
        evidenceItems.push({ value: evidence.bankCode });
      }
      break;
    case 'branchCode':
      if (evidence.branchCode) {
        evidenceItems.push({ value: evidence.branchCode });
      }
      break;
    case 'iban':
      if (evidence.iban) {
        evidenceItems.push({ value: evidence.iban });
      }
      break;
    case 'swiftCode':
      if (evidence.swiftCode) {
        evidenceItems.push({ value: evidence.swiftCode });
      }
      break;
    case 'accountNumber':
      if (evidence.accountNumber) {
        evidenceItems.push({ value: evidence.accountNumber });
      }
      break;
    case 'legalIdentifiers': {
      const { taxId, taxIdType, taxIdCountryCode, additionalTaxId, additionalTaxIdType } = evidence;
      if (taxId) {
        const idAndType = [taxId, taxIdType].filter(Boolean).join(' - ');
        const country = taxIdCountryCode ? ` (${taxIdCountryCode.toUpperCase()})` : '';
        // Carry the original parts so the edit dialog fills value/type/country directly on select,
        // instead of re-splitting the formatted `value` (breaks on " - " inside the identifier).
        evidenceItems.push({
          value: `${idAndType}${country}`,
          legalId: { value: taxId, typeName: taxIdType ?? null, countryCode: taxIdCountryCode ?? null },
        });
      }
      if (additionalTaxId) {
        const idAndType = [additionalTaxId, additionalTaxIdType].filter(Boolean).join(' - ');
        evidenceItems.push({
          value: idAndType,
          legalId: { value: additionalTaxId, typeName: additionalTaxIdType ?? null, countryCode: null },
        });
      }
      break;
    }
  }

  if (evidenceItems.length > 0 && typeLabel) {
    // Tag every item with its provider source (Lyons, EIN-Search, etc.) so each
    // preset in the Manual Processing dialog can display where it came from.
    for (let i = 0; i < evidenceItems.length; i++) {
      evidenceItems[i] = { ...evidenceItems[i], evidenceType: typeLabel };
    }
  }

  return evidenceItems.length > 0 ? evidenceItems : undefined;
}

function formatPartialMatchReasons(reasons: string[]): string | undefined {
  if (reasons.length === 0) return undefined;
  const labels = reasons.map((r) => PARTIAL_MATCH_REASON_LABELS[r] ?? r);
  return `Logic: ${labels.join(', ')}`;
}

const RESULT_PRIORITY: Record<string, number> = { Mismatch: 3, PartialMatch: 2, Match: 1, NotRelevant: 0 };

function worstResult(map: Record<string, { result: string; partialMatchReasons: string[] }>): string | undefined {
  const results = Object.values(map).map((v) => v.result);
  return results.reduce<string | undefined>(
    (worst, r) => (worst === undefined || (RESULT_PRIORITY[r] ?? -1) > (RESULT_PRIORITY[worst] ?? -1) ? r : worst),
    undefined,
  );
}

/**
 * Gets auto match result for a data point type
 */
function getAutoMatchResultForDataPoint(
  dataPointType: DataPointType,
  autoMatchDataSet: VerificationsAndAutoMatch['autoMatchResult']['autoMatchDataSet'],
  groupValue?: string,
): StatusType | undefined {
  if (!autoMatchDataSet) return 'Unknown' as StatusType;

  let result: string | undefined;

  if (dataPointType === 'legalIdentifiers') {
    if (!autoMatchDataSet.legalIdentifiersByValue) {
      result = undefined;
    } else if (groupValue !== undefined) {
      // Resolve this specific evidence group's result (one row per group value).
      result = autoMatchDataSet.legalIdentifiersByValue[groupValue]?.result;
    } else {
      // No group specified (fallback / single stacked row) — worst across all groups.
      result = worstResult(autoMatchDataSet.legalIdentifiersByValue);
    }
  } else {
    const matchData = ((): { result: string; partialMatchReasons: string[] } | null | undefined => {
      switch (dataPointType) {
        case 'country':
          return autoMatchDataSet.payeeCountry;
        case 'name':
          return autoMatchDataSet.payeeName;
        case 'address':
          return autoMatchDataSet.address;
        case 'accountCountry':
          return autoMatchDataSet.accountCountry;
        case 'bankName':
          return autoMatchDataSet.bankName;
        case 'bankCode':
          return autoMatchDataSet.bankCode;
        case 'branchCode':
          return autoMatchDataSet.branchCode;
        case 'swiftCode':
          return autoMatchDataSet.swift;
        case 'iban':
          return autoMatchDataSet.iban;
        case 'accountNumber':
          return autoMatchDataSet.accountNumber;
        case 'forFurtherCredit':
          return autoMatchDataSet.furtherCredit;
        default:
          return undefined;
      }
    })();
    result = matchData?.result;
  }

  if (result === 'Match') return 'Match';
  if (result === 'Mismatch') return 'Mismatch';
  if (result === 'PartialMatch') return 'PartialMatch';
  if (result === 'NotRelevant') return 'NotRelevant';
  return undefined;
}

/**
 * Gets partial match reasons for a data point type
 */
function getPartialMatchReasonsForDataPoint(
  dataPointType: DataPointType,
  autoMatchDataSet: VerificationsAndAutoMatch['autoMatchResult']['autoMatchDataSet'],
  groupValue?: string,
): string | undefined {
  if (!autoMatchDataSet) return undefined;

  if (dataPointType === 'legalIdentifiers') {
    if (!autoMatchDataSet.legalIdentifiersByValue) return undefined;
    if (groupValue !== undefined) {
      const group = autoMatchDataSet.legalIdentifiersByValue[groupValue];
      if (!group || group.result !== 'PartialMatch') return undefined;
      return formatPartialMatchReasons(group.partialMatchReasons);
    }
    const reasons = Object.values(autoMatchDataSet.legalIdentifiersByValue)
      .filter((v) => v.result === 'PartialMatch')
      .flatMap((v) => v.partialMatchReasons);
    return formatPartialMatchReasons(reasons);
  }

  const matchData = ((): { result: string; partialMatchReasons: string[] } | null | undefined => {
    switch (dataPointType) {
      case 'country':
        return autoMatchDataSet.payeeCountry;
      case 'name':
        return autoMatchDataSet.payeeName;
      case 'address':
        return autoMatchDataSet.address;
      case 'accountCountry':
        return autoMatchDataSet.accountCountry;
      case 'bankName':
        return autoMatchDataSet.bankName;
      case 'bankCode':
        return autoMatchDataSet.bankCode;
      case 'branchCode':
        return autoMatchDataSet.branchCode;
      case 'swiftCode':
        return autoMatchDataSet.swift;
      case 'iban':
        return autoMatchDataSet.iban;
      case 'accountNumber':
        return autoMatchDataSet.accountNumber;
      case 'forFurtherCredit':
        return autoMatchDataSet.furtherCredit;
      default:
        return undefined;
    }
  })();

  if (!matchData || matchData.result !== 'PartialMatch' || !matchData.partialMatchReasons) return undefined;

  return formatPartialMatchReasons(matchData.partialMatchReasons);
}

interface VerificationFieldResult {
  value: string | undefined;
  changedFromInitial: boolean;
}

/**
 * Gets manual/final processing value and change flag for a data point type
 */
function getVerificationValueForDataPoint(
  dataPointType: DataPointType,
  verification: PayeeWithAccountsVerificationV2,
): VerificationFieldResult {
  const { payee } = verification;
  if (!payee) return { value: undefined, changedFromInitial: false };

  const changed = (field: string): boolean => payee._changedFields?.has(field) ?? false;

  switch (dataPointType) {
    case 'country':
      return { value: extractStringValue(payee.countryCode), changedFromInitial: changed('countryCode') };
    case 'name':
      return { value: extractStringValue(payee.name), changedFromInitial: changed('name') };
    case 'address':
      return {
        value: extractStringValue(payee.address) || extractStringValue(payee.formattedAddress),
        changedFromInitial: changed('address') || changed('formattedAddress'),
      };
    case 'legalIdentifiers':
      // Handled as child rows in populatePayeeDetailsRows
      return { value: undefined, changedFromInitial: false };
    default:
      return { value: undefined, changedFromInitial: false };
  }
}

/**
 * Populates payee details rows with data from all sources
 */
/**
 * Formats a legal entity identifier to the standard format
 */
function formatLegalEntityIdentifier(lei: {
  value?: string | null;
  typeName?: string | null;
  countryCode?: string | null;
}): string | undefined {
  if (!lei.value) return undefined;
  return `${lei.value} - ${lei.typeName || ''} (${(lei.countryCode || '').toUpperCase()})`;
}

export function populatePayeeDetailsRows(
  initialRows: ExpandableTableRow[],
  record: SupplierValidationRecord,
  verificationData: SupplierValidationPayeeWithAccountsVerification,
  evidence: SupplierValidationVerificationRecordEvidence | undefined,
  verificationsAndAutoMatch: VerificationsAndAutoMatch,
  enrichmentEvidences: SupplierValidationVerificationRecordEvidence[],
): ExpandableTableRow[] {
  const populatedRows = initialRows.map((row) => {
    const vendorValue = getVendorValueForDataPoint(row.dataPointType, record);
    const primaryItems = getEvidenceValueForDataPoint(row.dataPointType, evidence) ?? [];
    const enrichmentItems = enrichmentEvidences.flatMap((e) => getEvidenceValueForDataPoint(row.dataPointType, e) ?? []);
    const mergedItems = dedupeEvidenceItems([...primaryItems, ...enrichmentItems]);
    const evidenceValue = mergedItems.length > 0 ? mergedItems : undefined;
    const autoMatchValue = getAutoMatchResultForDataPoint(
      row.dataPointType,
      verificationsAndAutoMatch.autoMatchResult?.autoMatchDataSet,
    );
    const partialMatchReasons = getPartialMatchReasonsForDataPoint(
      row.dataPointType,
      verificationsAndAutoMatch.autoMatchResult?.autoMatchDataSet,
    );
    const manualResult = getVerificationValueForDataPoint(row.dataPointType, verificationsAndAutoMatch.manualVerification);
    const finalResult = getVerificationValueForDataPoint(row.dataPointType, verificationsAndAutoMatch.finalVerification);

    return {
      ...row,
      vendor: vendorValue,
      evidence: evidenceValue,
      automatedMatching: autoMatchValue,
      reason: partialMatchReasons || row.reason,
      // Null saved field on an existing payee => user marked "Not relevant" (getVerificationValueForDataPoint
      // returns undefined for a null field). Empty string => unset (ordinary empty cell). No payee => undefined
      // everywhere, which must stay an ordinary empty cell, so guard on the payee existing.
      manualProcess:
        verificationsAndAutoMatch.manualVerification.payee && manualResult.value === undefined
          ? NOT_RELEVANT
          : manualResult.value,
      finalReview:
        verificationsAndAutoMatch.finalVerification.payee && finalResult.value === undefined ? NOT_RELEVANT : finalResult.value,
      manualChangedFromInitial: manualResult.changedFromInitial,
      finalChangedFromInitial: finalResult.changedFromInitial,
    };
  });

  // Add additionalName, additionalAddress, and multiple legal entity identifiers as child rows if they exist
  const result: ExpandableTableRow[] = [];
  const { payee } = verificationData;
  const manualPayee = verificationsAndAutoMatch.manualVerification.payee;
  const finalPayee = verificationsAndAutoMatch.finalVerification.payee;

  for (let i = 0; i < populatedRows.length; i++) {
    const row = populatedRows[i];

    // Handle Legal Identifiers row — one row per evidence value-group (grouped by legalId.value),
    // each with its own auto-match result. Falls back to a single stacked row when there are no groups.
    if (row.dataPointType === 'legalIdentifiers') {
      const { supplierRegistrationProcess } = record;
      const vendorLegalId = supplierRegistrationProcess?.legalId
        ? {
            value: supplierRegistrationProcess.legalId,
            typeName: supplierRegistrationProcess.legalIdType,
            countryCode: supplierRegistrationProcess.countryCode,
          }
        : null;

      const manualLeis = manualPayee?.legalEntityIdentifiers || [];
      const finalLeis = finalPayee?.legalEntityIdentifiers || [];

      const legalIdPrimaryItems = getEvidenceValueForDataPoint('legalIdentifiers', evidence) ?? [];
      const legalIdEnrichmentItems = enrichmentEvidences.flatMap(
        (e) => getEvidenceValueForDataPoint('legalIdentifiers', e) ?? [],
      );
      const legalIdMergedItems = dedupeEvidenceItems([...legalIdPrimaryItems, ...legalIdEnrichmentItems]);

      // Vendor is an EvidenceItem[] (not a plain string) so the preset carries the original legal-id
      // parts — the edit dialog fills value/type/country from these on select instead of re-splitting
      // the formatted string (which breaks when the identifier value contains " - ").
      const vendorFormatted = vendorLegalId ? formatLegalEntityIdentifier(vendorLegalId) : undefined;
      const vendorItem: EvidenceItemType[] | undefined =
        vendorLegalId && vendorFormatted
          ? [
              {
                value: vendorFormatted,
                legalId: {
                  value: vendorLegalId.value,
                  typeName: vendorLegalId.typeName ?? null,
                  countryCode: vendorLegalId.countryCode ?? null,
                },
              },
            ]
          : undefined;

      const autoMatchDataSet = verificationsAndAutoMatch.autoMatchResult?.autoMatchDataSet;
      const legalIdentifiersByValue = autoMatchDataSet?.legalIdentifiersByValue ?? null;
      const hasGroups = !!legalIdentifiersByValue && Object.keys(legalIdentifiersByValue).length > 0;

      if (hasGroups) {
        // Build one group per key in legalIdentifiersByValue (backend order), attaching the merged
        // evidence items whose legalId.value matches that key.
        const groups = Object.keys(legalIdentifiersByValue).map((groupValue) => ({
          value: groupValue,
          evidenceItems: legalIdMergedItems.filter((item) => item.legalId?.value === groupValue),
        }));

        // Place the group matching the vendor registration LEI first, so Vendor + Evidence align on row 1.
        if (vendorLegalId?.value) {
          const vendorIdx = groups.findIndex((g) => g.value === vendorLegalId.value);
          if (vendorIdx > 0) {
            const [vendorGroup] = groups.splice(vendorIdx, 1);
            groups.unshift(vendorGroup);
          }
        }

        // A saved (manual/final) LEI belongs on the row of the evidence group with the same value, not
        // at its positional index — otherwise a saved "GB849154403" would render next to the "05259236"
        // group's evidence/result. Match by value; any saved LEI matching no group falls through to an
        // extra positional row after the group rows (preserving the "saved LEI beyond groups" behavior).
        const groupValues = new Set(groups.map((g) => g.value));
        const leftoverManualLeis = manualLeis.filter((l) => !l.value || !groupValues.has(l.value));
        const leftoverFinalLeis = finalLeis.filter((l) => !l.value || !groupValues.has(l.value));
        const leftoverCount = Math.max(leftoverManualLeis.length, leftoverFinalLeis.length);
        const rowCount = groups.length + leftoverCount;

        for (let idx = 0; idx < rowCount; idx++) {
          const group = groups[idx];
          // Group rows take the saved LEI matching the group's value; leftover rows take positional leftovers.
          const manualLei = group ? manualLeis.find((l) => l.value === group.value) : leftoverManualLeis[idx - groups.length];
          const finalLei = group ? finalLeis.find((l) => l.value === group.value) : leftoverFinalLeis[idx - groups.length];
          const groupEvidence = group && group.evidenceItems.length > 0 ? group.evidenceItems : undefined;
          const autoMatchForRow = getAutoMatchResultForDataPoint('legalIdentifiers', autoMatchDataSet, group?.value);
          const reasonForRow = getPartialMatchReasonsForDataPoint('legalIdentifiers', autoMatchDataSet, group?.value);

          if (idx === 0) {
            result.push({
              ...row,
              vendor: vendorItem,
              evidence: groupEvidence,
              automatedMatching: group ? autoMatchForRow : 'NotRelevant',
              reason: reasonForRow || undefined,
              manualProcess: formatLegalEntityIdentifier(manualLei || {}),
              finalReview: formatLegalEntityIdentifier(finalLei || {}),
              manualChangedFromInitial: manualLei?.hasChangedFromInitialState ?? false,
              finalChangedFromInitial: finalLei?.hasChangedFromInitialState ?? false,
              showDeleteIcon: false,
            });
          } else {
            result.push({
              dataPoint: generateDataPointName('Legal Identifier', idx + 1),
              dataPointType: 'legalIdentifiers',
              vendor: undefined,
              evidence: groupEvidence,
              // Group-derived rows show their group result; positional-only rows (no group) are NotRelevant.
              automatedMatching: group ? (autoMatchForRow ?? 'NotRelevant') : 'NotRelevant',
              reason: reasonForRow || undefined,
              manualProcess: formatLegalEntityIdentifier(manualLei || {}),
              finalReview: formatLegalEntityIdentifier(finalLei || {}),
              manualChangedFromInitial: manualLei?.hasChangedFromInitialState ?? false,
              finalChangedFromInitial: finalLei?.hasChangedFromInitialState ?? false,
              isChildRow: true,
              // System-derived group rows are not deletable; a positional-only saved LEI keeps today's delete icon.
              showDeleteIcon: !group,
            });
          }
        }

        continue;
      }

      // Fallback: no evidence groups — today's behavior (single stacked parent + manual/final child rows).
      const maxCount = Math.max(vendorLegalId ? 1 : 0, manualLeis.length, finalLeis.length);
      const legalIdEvidenceValue = legalIdMergedItems.length > 0 ? legalIdMergedItems : undefined;

      result.push({
        ...row,
        vendor: vendorItem,
        evidence: legalIdEvidenceValue,
        manualProcess: formatLegalEntityIdentifier(manualLeis[0] || {}),
        finalReview: formatLegalEntityIdentifier(finalLeis[0] || {}),
        manualChangedFromInitial: manualLeis[0]?.hasChangedFromInitialState ?? false,
        finalChangedFromInitial: finalLeis[0]?.hasChangedFromInitialState ?? false,
        showDeleteIcon: false,
      });

      for (let idx = 1; idx < maxCount; idx++) {
        const manualLei = manualLeis[idx];
        const finalLei = finalLeis[idx];

        if (manualLei || finalLei) {
          result.push({
            dataPoint: generateDataPointName('Legal Identifier', idx + 1),
            dataPointType: 'legalIdentifiers',
            vendor: undefined,
            evidence: undefined,
            automatedMatching: 'NotRelevant',
            manualProcess: formatLegalEntityIdentifier(manualLei || {}),
            finalReview: formatLegalEntityIdentifier(finalLei || {}),
            manualChangedFromInitial: manualLei?.hasChangedFromInitialState ?? false,
            finalChangedFromInitial: finalLei?.hasChangedFromInitialState ?? false,
            isChildRow: true,
            showDeleteIcon: true,
          });
        }
      }

      continue;
    }

    // For all other rows, just add them as-is
    result.push(row);

    // Add Additional Name as separate row if it exists and this is the Name row
    if (row.dataPointType === 'name') {
      // Vendor column surfaces the registration's additionalCompanyName (from supplierValidationProcess),
      // falling back to the verification payee's additionalName when the registration has none.
      const additionalNameVendor =
        record.supplierRegistrationProcess?.additionalCompanyName || extractStringValue(payee?.additionalName);
      const additionalNameManual = extractStringValue(manualPayee?.additionalName);
      const additionalNameFinal = extractStringValue(finalPayee?.additionalName);

      // Only add if there's a value in at least one source
      if (additionalNameVendor || additionalNameManual || additionalNameFinal) {
        result.push({
          dataPoint: 'Additional Name',
          dataPointType: 'name',
          vendor: additionalNameVendor,
          evidence: undefined, // Evidence doesn't typically have separate entries for additional fields
          // 'Unknown' (not 'NotRelevant') so an empty Manual Processing / Final Review cell renders as
          // an actionable empty field — the same appearance as every other unfilled row and as a
          // manually-added Additional Name sub-row — instead of a gray "Not relevant" cell.
          automatedMatching: 'Unknown',
          manualProcess: additionalNameManual,
          finalReview: additionalNameFinal,
          showDeleteIcon: !!(additionalNameManual || additionalNameFinal),
          // Mark as a child row so the request builders (which locate the additional value via
          // `dataPointType === 'name' && isChildRow`) include edits to it on save.
          isChildRow: true,
        });
      }
    }

    // Add Additional Address as separate row if it exists and this is the Address row
    if (row.dataPointType === 'address') {
      const additionalAddressVendor = extractStringValue(payee?.additionalAddress);
      const additionalAddressManual = extractStringValue(manualPayee?.additionalAddress);
      const additionalAddressFinal = extractStringValue(finalPayee?.additionalAddress);

      // Only add if there's a value in at least one source
      if (additionalAddressVendor || additionalAddressManual || additionalAddressFinal) {
        result.push({
          dataPoint: 'Additional Address',
          dataPointType: 'address',
          vendor: additionalAddressVendor,
          evidence: undefined, // Evidence doesn't typically have separate entries for additional fields
          automatedMatching: 'NotRelevant', // Additional fields typically don't have auto matching
          manualProcess: additionalAddressManual,
          finalReview: additionalAddressFinal,
          showDeleteIcon: !!(additionalAddressManual || additionalAddressFinal),
          // Mark as a child row so the request builders (which locate the additional value via
          // `dataPointType === 'address' && isChildRow`) include edits to it on save.
          isChildRow: true,
        });
      }
    }
  }

  return result;
}

/**
 * Populates account details rows with data from all sources
 */
/**
 * Extracts the display value for a given account data point directly from an AccountVerificationRecordV2,
 * converting country codes to names where applicable.
 */
function getAccountVerificationValueForDataPoint(
  dataPointType: DataPointType,
  account: AccountVerificationRecordV2,
): string | undefined {
  switch (dataPointType) {
    case 'accountCountry': {
      // Preserve the null-vs-empty-string distinction the caller's toCell() relies on: a saved null
      // means "Not relevant" (-> undefined here), an empty string is an ordinary empty cell (-> ''),
      // and only a real code is formatted for display. Collapsing '' to undefined (via a falsy check)
      // wrongly rendered an empty account country as "Not relevant".
      const code = account.countryCode;
      if (code === null || code === undefined) return undefined;
      return code ? formatCountryDisplay(code) : code;
    }
    case 'bankName':
      return extractStringValue(account.bankName);
    case 'bankCode':
      return extractStringValue(account.bankCode);
    case 'branchCode':
      return extractStringValue(account.branchCode);
    case 'iban':
      return extractStringValue(account.iban);
    case 'swiftCode':
      return extractStringValue(account.swift);
    case 'accountNumber':
      return extractStringValue(account.accountNumber);
    case 'forFurtherCredit':
      return extractStringValue(account.furtherCredit);
    default:
      return undefined;
  }
}

/**
 * Auto-match result for the "Additional Bank Code" (ABA/Fedwire) row. The row's dataPointType is
 * 'bankCode' — the same as the main Bank Code row — so it cannot be resolved through
 * getAutoMatchResultForDataPoint (which would return the main bank code's result). It reads the
 * dedicated autoMatchDataSet.abaRouting slot instead. Falls back to 'Unknown' when the backend
 * sends no ABA result (an older response, or no ABA comparison was run).
 */
export function getAbaRoutingAutoMatchResult(
  autoMatchDataSet: VerificationsAndAutoMatch['autoMatchResult']['autoMatchDataSet'] | undefined,
): StatusType {
  const result = autoMatchDataSet?.abaRouting?.result;
  if (result === 'Match') return 'Match';
  if (result === 'Mismatch') return 'Mismatch';
  if (result === 'PartialMatch') return 'PartialMatch';
  if (result === 'NotRelevant') return 'NotRelevant';
  return 'Unknown' as StatusType;
}

/** Partial-match reason text for the ABA/Fedwire row, or undefined when it is not a partial match. */
export function getAbaRoutingPartialMatchReasons(
  autoMatchDataSet: VerificationsAndAutoMatch['autoMatchResult']['autoMatchDataSet'] | undefined,
): string | undefined {
  const aba = autoMatchDataSet?.abaRouting;
  if (!aba || aba.result !== 'PartialMatch' || !aba.partialMatchReasons) return undefined;
  return formatPartialMatchReasons(aba.partialMatchReasons);
}

/**
 * Fills the "Additional Bank Code" (ABA/Fedwire) row from a merged secondary account, mutating
 * `rows` in place. applyAbaRoutingVendor (in populateAccountDetailsRows) may already have created
 * the row to show the registration ABA in its Vendor column — reuse it so there is exactly one
 * Additional Bank Code row; otherwise insert one right after the main Bank Code row.
 *
 * The secondary account's bankCode goes into Manual/Final, and its persisted change flag
 * (_changedFields.has('bankCode')) is carried onto manual/finalChangedFromInitial so the cell
 * renders orange (edited) after a save/reload — without this the row stayed green even though the
 * server marked the bank code changed-from-initial. Auto-match comes from the abaRouting slot.
 */
export function fillAdditionalBankCodeRow(
  rows: ExpandableTableRow[],
  secManual: AccountVerificationRecordV2 | undefined,
  secFinal: AccountVerificationRecordV2 | undefined,
  autoMatchDataSet?: VerificationsAndAutoMatch['autoMatchResult']['autoMatchDataSet'],
): ExpandableTableRow[] {
  const secManualChanged = secManual?._changedFields?.has('bankCode') ?? false;
  const secFinalChanged = secFinal?._changedFields?.has('bankCode') ?? false;

  // Mirror toCell() in populateAccountDetailsRows: when the secondary account EXISTS but its
  // bankCode is null, the operator marked the Additional Bank Code "Not relevant" — render the
  // sentinel so the cell shows "Not relevant" instead of an empty field. An empty string stays an
  // ordinary empty cell; an ABSENT account (secManual/secFinal undefined) keeps '' (never "Not
  // relevant"). Without this a saved-null bankCode collapsed to '' and the cell looked unset.
  const toCell = (account: AccountVerificationRecordV2 | undefined): string =>
    account ? (account.bankCode ?? NOT_RELEVANT) : '';

  const existingIndex = rows.findIndex((r) => r.dataPointType === 'bankCode' && r.dataPoint === ADDITIONAL_BANK_CODE_LABEL);
  if (existingIndex >= 0) {
    rows[existingIndex] = {
      ...rows[existingIndex],
      manualProcess: toCell(secManual),
      finalReview: toCell(secFinal),
      manualChangedFromInitial: secManualChanged,
      finalChangedFromInitial: secFinalChanged,
      showDeleteIcon: true,
    };
    return rows;
  }

  const bankCodeRowIndex = rows.findIndex((r) => r.dataPointType === 'bankCode');
  if (bankCodeRowIndex < 0) {
    return rows;
  }
  const additionalRow: ExpandableTableRow = {
    dataPoint: ADDITIONAL_BANK_CODE_LABEL,
    dataPointType: 'bankCode',
    vendor: '-',
    evidence: undefined,
    automatedMatching: getAbaRoutingAutoMatchResult(autoMatchDataSet),
    reason: getAbaRoutingPartialMatchReasons(autoMatchDataSet),
    manualProcess: toCell(secManual),
    finalReview: toCell(secFinal),
    manualChangedFromInitial: secManualChanged,
    finalChangedFromInitial: secFinalChanged,
    isChildRow: true,
    showDeleteIcon: true,
  };
  rows.splice(bankCodeRowIndex + 1, 0, additionalRow);
  return rows;
}

/**
 * Display-only: surfaces the registration's ABA routing number in the Bank Code area's Vendor
 * column. When abaRouting and accountDetails.bankCode both exist and differ, ensures a single
 * "Additional Bank Code" row carries abaRouting in its Vendor column. Returns a new rows array.
 */
export function applyAbaRoutingVendor(
  rows: ExpandableTableRow[],
  record: SupplierValidationRecord,
  autoMatchDataSet?: VerificationsAndAutoMatch['autoMatchResult']['autoMatchDataSet'],
): ExpandableTableRow[] {
  const aba = record.supplierRegistrationProcess?.abaRouting || undefined;
  const bc = record.supplierRegistrationProcess?.accountDetails?.bankCode || undefined;

  // Only when both exist and differ does ABA become a separate "Additional Bank Code" vendor.
  // (bc empty is handled by the Bank Code row's vendor fallback; aba===bc needs no extra row.)
  if (!aba || !bc || aba === bc) {
    return rows;
  }

  // ABA/Fedwire auto-match comes from its own autoMatchDataSet.abaRouting slot (not the main
  // bankCode result), defaulting to 'Unknown' when the backend sends none.
  const abaAutoMatch = getAbaRoutingAutoMatchResult(autoMatchDataSet);
  const abaReason = getAbaRoutingPartialMatchReasons(autoMatchDataSet);

  const existingIndex = rows.findIndex((r) => r.dataPointType === 'bankCode' && r.dataPoint === ADDITIONAL_BANK_CODE_LABEL);
  if (existingIndex >= 0) {
    const next = [...rows];
    next[existingIndex] = { ...next[existingIndex], vendor: aba, automatedMatching: abaAutoMatch, reason: abaReason };
    return next;
  }

  const bankCodeIndex = rows.findIndex((r) => r.dataPointType === 'bankCode');
  if (bankCodeIndex < 0) {
    return rows;
  }
  const additionalRow: ExpandableTableRow = {
    dataPoint: ADDITIONAL_BANK_CODE_LABEL,
    dataPointType: 'bankCode',
    vendor: aba,
    evidence: undefined,
    automatedMatching: abaAutoMatch,
    reason: abaReason,
    manualProcess: '',
    finalReview: '',
    isChildRow: true,
    showDeleteIcon: false,
  };
  const next = [...rows];
  next.splice(bankCodeIndex + 1, 0, additionalRow);
  return next;
}

/** Formats a DB-sourced bank-name attribution, e.g. "(DB - bank code: 401276)". */
export function dbBankNameSource(label: string, code: string | null | undefined): string {
  return code ? `(DB - ${label}: ${code})` : `(DB - ${label})`;
}

/**
 * Registration-side (Vendor column) DB bank-name items for the bankName row.
 * Order: swift, bank code, ABA routing, then the vendor's own value.
 * The ABA item is dropped when it duplicates the bank-code item — same bankCode value
 * AND the bank-code item already carries a name to show. When the codes match but the
 * bank-code source has no name, the ABA item is kept so the only name is not lost.
 */
export function buildRegistrationDbBankNameItems(
  dbEvidence: BankNameDatabaseEvidenceServerResponse | null | undefined,
  vendorValue: string | null | undefined,
): EvidenceItemType[] {
  const countryBankCode = dbEvidence?.registrationCountryBankCodeData?.bankCode;
  const countryBankCodeHasName = !!dbEvidence?.registrationCountryBankCodeData?.bankName;
  const abaBankCode = dbEvidence?.registrationAbaRoutingData?.bankCode;
  const abaDuplicatesBankCode = !!abaBankCode && abaBankCode === countryBankCode && countryBankCodeHasName;

  return [
    dbEvidence?.registrationSwiftData?.bankName
      ? {
          value: dbEvidence.registrationSwiftData.bankName,
          source: dbBankNameSource('swift', dbEvidence.registrationSwiftData.swiftCode),
        }
      : null,
    dbEvidence?.registrationCountryBankCodeData?.bankName
      ? {
          value: dbEvidence.registrationCountryBankCodeData.bankName,
          source: dbBankNameSource('bank code', dbEvidence.registrationCountryBankCodeData.bankCode),
        }
      : null,
    dbEvidence?.registrationAbaRoutingData?.bankName && !abaDuplicatesBankCode
      ? {
          value: dbEvidence.registrationAbaRoutingData.bankName,
          source: dbBankNameSource('ABA routing', dbEvidence.registrationAbaRoutingData.bankCode),
        }
      : null,
    vendorValue ? { value: vendorValue } : null,
  ].filter((item): item is EvidenceItemType => item !== null);
}

/**
 * Evidence-side (Evidence column) DB bank-name items for the bankName row, gathered from
 * evidenceDataPoints. Each source shows the code it was resolved from — same format as the
 * registration side — so identical bank names are distinguishable.
 */
export function buildEvidenceDbBankNameItems(
  dbEvidence: BankNameDatabaseEvidenceServerResponse | null | undefined,
): EvidenceItemType[] {
  if (!dbEvidence) {
    return [];
  }
  return (dbEvidence.evidenceDataPoints ?? []).flatMap((point) => {
    const items: EvidenceItemType[] = [];
    if (point.countryBankCodeData?.bankName) {
      items.push({
        value: point.countryBankCodeData.bankName,
        source: dbBankNameSource('bank code', point.countryBankCodeData.bankCode),
      });
    }
    if (point.swiftData?.bankName) {
      items.push({
        value: point.swiftData.bankName,
        source: dbBankNameSource('swift', point.swiftData.swiftCode),
      });
    }
    return items;
  });
}

/**
 * Removes duplicate evidence items that share the same value AND the same source, keeping the first
 * occurrence and preserving order. Two items with the same bank name but different sources (e.g. one
 * resolved via bank code, another via swift) are kept as distinct. Items without a source are keyed
 * on value alone.
 */
export function dedupeEvidenceItems(items: EvidenceItemType[]): EvidenceItemType[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = `${item.value} ${item.source ?? ''} ${item.evidenceType ?? ''}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/**
 * True when the account is Mexican (MX) and therefore needs a CLABE (localFormat) row.
 * MX is detected from the registration account country OR from any evidence's account country
 * (primary evidence or enrichment evidences). Not derived from the live-edited Account Country
 * cell, so the CLABE row does not flicker while the operator edits the country.
 */
function recordHasMexicoAccountCountry(
  record: SupplierValidationRecord,
  evidence: SupplierValidationVerificationRecordEvidence | undefined,
  enrichmentEvidences: SupplierValidationVerificationRecordEvidence[],
): boolean {
  // Country codes are compared case-insensitively — the registration/evidence value may be
  // lowercase (matches how morteeRegistrationForms compares accountBankCountryCode).
  const isMx = (code: string | null | undefined): boolean => code?.toUpperCase() === COUNTRY_CODES.Mexico;
  if (isMx(record.supplierRegistrationProcess?.accountBankCountryCode)) {
    return true;
  }
  return [evidence, ...enrichmentEvidences].some((e) => isMx(e?.data?.accountCountryCode));
}

export function populateAccountDetailsRows(
  initialRows: ExpandableTableRow[],
  record: SupplierValidationRecord,
  evidence: SupplierValidationVerificationRecordEvidence | undefined,
  verificationsAndAutoMatch: VerificationsAndAutoMatch,
  accountIndex: number,
  enrichmentEvidences: SupplierValidationVerificationRecordEvidence[] = [],
): ExpandableTableRow[] {
  const dbEvidence = verificationsAndAutoMatch.databaseEvidence;
  const manualAccount = verificationsAndAutoMatch.manualVerification.accounts[accountIndex];
  const finalAccount = verificationsAndAutoMatch.finalVerification.accounts[accountIndex];

  const accountFieldNameForDataPoint = (dataPointType: DataPointType): string | null => {
    switch (dataPointType) {
      case 'accountCountry':
        return 'countryCode';
      case 'bankName':
        return 'bankName';
      case 'bankCode':
        return 'bankCode';
      case 'branchCode':
        return 'branchCode';
      case 'iban':
        return 'iban';
      case 'swiftCode':
        return 'swift';
      case 'accountNumber':
        return 'accountNumber';
      case 'forFurtherCredit':
        return 'furtherCredit';
      default:
        return null;
    }
  };

  const populatedRows = initialRows.map((row) => {
    const vendorValue = getAccountVendorValueForDataPoint(row.dataPointType, record);

    // For bankName, supplement vendor value with database evidence bank names. Each DB source
    // shows which bank code it was resolved from, so identical bank names are distinguishable.
    const bankNameVendorItems: EvidenceItemType[] | undefined =
      row.dataPointType === 'bankName' ? buildRegistrationDbBankNameItems(dbEvidence, vendorValue) : undefined;
    const primaryItems = getEvidenceValueForDataPoint(row.dataPointType, evidence) ?? [];
    const enrichmentItems = enrichmentEvidences.flatMap((e) => getEvidenceValueForDataPoint(row.dataPointType, e) ?? []);

    const dbEvidenceItems: EvidenceItemType[] = row.dataPointType === 'bankName' ? buildEvidenceDbBankNameItems(dbEvidence) : [];

    const allItems = dedupeEvidenceItems([...primaryItems, ...enrichmentItems, ...dbEvidenceItems]);
    const evidenceValue = allItems.length > 0 ? allItems : undefined;
    const autoMatchValue = getAutoMatchResultForDataPoint(
      row.dataPointType,
      verificationsAndAutoMatch.autoMatchResult?.autoMatchDataSet,
    );
    const partialMatchReasons = getPartialMatchReasonsForDataPoint(
      row.dataPointType,
      verificationsAndAutoMatch.autoMatchResult?.autoMatchDataSet,
    );

    const fieldName = accountFieldNameForDataPoint(row.dataPointType);
    const manualChangedFromInitial = fieldName ? (manualAccount?._changedFields?.has(fieldName) ?? false) : false;
    const finalChangedFromInitial = fieldName ? (finalAccount?._changedFields?.has(fieldName) ?? false) : false;

    // Null and empty-string carry distinct meaning: a saved null is a field the user marked
    // "Not relevant" (reconstruct the sentinel so the cell re-renders as "Not relevant"); a saved
    // empty string is simply an unset field (ordinary empty cell). extractStringValue (inside
    // getAccountVerificationValueForDataPoint) maps API null -> undefined and "" -> "", so undefined
    // here is the null case. Only applied when the account exists — an absent account leaves the cell
    // undefined (ordinary empty), never "Not relevant".
    const toCell = (value: string | undefined): string | undefined => (value === undefined ? NOT_RELEVANT : value);

    const manualProcessValue = manualAccount
      ? toCell(getAccountVerificationValueForDataPoint(row.dataPointType, manualAccount))
      : undefined;
    const finalReviewValue = finalAccount
      ? toCell(getAccountVerificationValueForDataPoint(row.dataPointType, finalAccount))
      : undefined;

    return {
      ...row,
      vendor:
        bankNameVendorItems !== undefined ? (bankNameVendorItems.length > 0 ? bankNameVendorItems : undefined) : vendorValue,
      evidence: evidenceValue,
      automatedMatching: autoMatchValue,
      reason: partialMatchReasons || row.reason,
      manualProcess: manualProcessValue,
      finalReview: finalReviewValue,
      manualChangedFromInitial,
      finalChangedFromInitial,
    };
  });

  const rowsWithAba = applyAbaRoutingVendor(populatedRows, record, verificationsAndAutoMatch.autoMatchResult?.autoMatchDataSet);

  if (!recordHasMexicoAccountCountry(record, evidence, enrichmentEvidences)) {
    return rowsWithAba;
  }

  // Same null/empty-string handling as toCell() above (which is out of scope here, being local to
  // the .map() callback): a saved null (-> extractStringValue undefined) means "Not relevant".
  const toCellForClabe = (value: string | undefined): string | undefined => (value === undefined ? NOT_RELEVANT : value);

  const clabeRow: ExpandableTableRow = {
    dataPoint: 'CLABE',
    dataPointType: 'clabe',
    vendor: record.supplierRegistrationProcess?.accountDetails?.accountNumber || undefined,
    evidence: undefined,
    automatedMatching: 'Unknown',
    manualProcess: manualAccount ? toCellForClabe(extractStringValue(manualAccount.localFormat)) : undefined,
    finalReview: finalAccount ? toCellForClabe(extractStringValue(finalAccount.localFormat)) : undefined,
    manualChangedFromInitial: manualAccount?._changedFields?.has('localFormat') ?? false,
    finalChangedFromInitial: finalAccount?._changedFields?.has('localFormat') ?? false,
  };

  const accountNumberIndex = rowsWithAba.findIndex((r) => r.dataPointType === 'accountNumber');
  const next = [...rowsWithAba];
  // Insert right after Account Number; if that row is somehow absent, append at the end.
  next.splice(accountNumberIndex >= 0 ? accountNumberIndex + 1 : next.length, 0, clabeRow);
  return next;
}

/** Fields compared when deciding two accounts are identical-except-bankCode (see spec). */
const MERGE_COMPARED_FIELDS: Array<keyof AccountVerificationRecordV2> = [
  'countryCode',
  'bankName',
  'branchCode',
  'iban',
  'swift',
  'accountNumber',
  'furtherCredit',
  'type',
  'isPrivate',
];

/**
 * True iff both accounts are defined and equal on every compared field (bankCode ignored).
 */
export function accountsMergeableExceptBankCode(
  a: AccountVerificationRecordV2 | undefined,
  b: AccountVerificationRecordV2 | undefined,
): boolean {
  if (!a || !b) return false;
  return MERGE_COMPARED_FIELDS.every((field) => a[field] === b[field]);
}

export type BankCodeMergeDescriptor = { primaryIndex: number; secondaryIndex?: number };

/**
 * Pairs accounts that are identical-except-bankCode in EITHER flow into one merged table.
 * Primary is the lower staticId; at most one secondary per primary (cap 1 additional bank
 * code). A third identical-except-bankCode account stays a separate table.
 *
 * When a `record` is provided, the primary/secondary roles of a merged pair are re-ordered so the
 * primary "Bank Code" row holds the account matching the registration's main bank code
 * (accountDetails.bankCode) and the secondary "Additional Bank Code" row holds the ABA/Fedwire
 * account (abaRouting). The Vendor column is anchored to those same registration values regardless
 * of the verification accounts' staticId order, so without this the ABA account could land in the
 * Bank Code row (and the main bank code in Additional) — visually swapping the two columns.
 */
export function mergeAdditionalBankCodeAccounts(
  manualAccounts: AccountVerificationRecordV2[],
  finalAccounts: AccountVerificationRecordV2[],
  record?: SupplierValidationRecord,
): BankCodeMergeDescriptor[] {
  // Iterate over the UNION of manual and final indices, not just manual. The two flows come from
  // separate endpoints and one can be empty while the other is populated (e.g. a record whose
  // accounts only exist in the Final flow). Ordering by manualAccounts alone would return [] in that
  // case and the account tables would not render at all. Sort by whichever flow has an account at
  // the index (prefer manual, fall back to final) so the primary/lowest-staticId rule still holds.
  const accountCount = Math.max(manualAccounts.length, finalAccounts.length);
  const staticIdAt = (index: number): string => (manualAccounts[index] ?? finalAccounts[index])?.staticId ?? '';
  const order = Array.from({ length: accountCount }, (_, index) => index).sort((x, y) =>
    staticIdAt(x) < staticIdAt(y) ? -1 : 1,
  );

  // Registration values that anchor which merged account is the "main" bank code vs the ABA one.
  // The Vendor column always shows accountDetails.bankCode on the Bank Code row and abaRouting on
  // the Additional Bank Code row, so the verification account matching each is placed on the same
  // row (see reorderPairByRegistration below).
  const registrationBankCode = trimToNull(record?.supplierRegistrationProcess?.accountDetails?.bankCode ?? null);
  const registrationAbaRouting = trimToNull(record?.supplierRegistrationProcess?.abaRouting ?? null);

  // Given a merged pair, decide whether the roles should be swapped so the primary holds the main
  // bank code and the secondary holds the ABA/Fedwire code. Uses whichever flow (manual, then final)
  // has both accounts. A swap fires when the current secondary matches the registration bank code,
  // or the current primary matches the ABA routing — i.e. the roles are backwards. When neither
  // side matches cleanly, the original staticId order is kept.
  const bankCodeAt = (index: number): string | null =>
    trimToNull((manualAccounts[index] ?? finalAccounts[index])?.bankCode ?? null);
  const reorderPairByRegistration = (primaryIndex: number, secondaryIndex: number): BankCodeMergeDescriptor => {
    if (!registrationBankCode && !registrationAbaRouting) {
      return { primaryIndex, secondaryIndex };
    }
    const primaryCode = bankCodeAt(primaryIndex);
    const secondaryCode = bankCodeAt(secondaryIndex);
    const secondaryIsMainBankCode = !!registrationBankCode && secondaryCode === registrationBankCode;
    const primaryIsAba = !!registrationAbaRouting && primaryCode === registrationAbaRouting;
    if (secondaryIsMainBankCode || primaryIsAba) {
      return { primaryIndex: secondaryIndex, secondaryIndex: primaryIndex };
    }
    return { primaryIndex, secondaryIndex };
  };

  const consumed = new Set<number>();
  const descriptors: BankCodeMergeDescriptor[] = [];

  for (const i of order) {
    if (consumed.has(i)) continue;
    consumed.add(i);

    let secondaryIndex: number | undefined;
    for (const j of order) {
      if (consumed.has(j)) continue;
      // Two accounts merge into one table (primary + "Additional Bank Code" row) when, within a
      // single flow, they are identical-except-bankCode AND their bank codes either differ or are
      // both empty. Merging is decided per flow — not by combining a cross-flow bankCode diff with a
      // separate mergeability check — so a match in the manual flow can't be spuriously triggered by
      // a bankCode difference that only exists in the final flow (or vice versa). The two flows come
      // from separate endpoints and can diverge (e.g. final not yet filled), so a match in EITHER
      // flow is enough. accountsMergeableExceptBankCode returns false on an undefined account, so a
      // missing/short flow simply doesn't contribute a match instead of throwing.
      //
      // "Both empty" (both bank codes null/blank) counts as a merge: those are the same account with
      // no bank code yet, not two real accounts. Two accounts sharing the SAME non-empty bankCode are
      // still treated as true duplicates and kept as separate tables.
      const shouldMergeInFlow = (
        a: AccountVerificationRecordV2 | undefined,
        b: AccountVerificationRecordV2 | undefined,
      ): boolean => {
        if (!accountsMergeableExceptBankCode(a, b)) return false;
        const aEmpty = isEmptyValue(a!.bankCode ?? undefined);
        const bEmpty = isEmptyValue(b!.bankCode ?? undefined);
        return a!.bankCode !== b!.bankCode || (aEmpty && bEmpty);
      };
      if (shouldMergeInFlow(manualAccounts[i], manualAccounts[j]) || shouldMergeInFlow(finalAccounts[i], finalAccounts[j])) {
        secondaryIndex = j;
        consumed.add(j);
        break; // cap: only one additional bank code per table
      }
    }

    descriptors.push(secondaryIndex === undefined ? { primaryIndex: i } : reorderPairByRegistration(i, secondaryIndex));
  }

  return descriptors;
}

/**
 * Parses a formatted legal identifier string back into its parts. Handles the full
 * "value - typeName (countryCode)" form as well as the partial forms the edit dialog produces when
 * the country and/or type are omitted: "value - typeName ()", "value - typeName", and "value".
 * The typeName is resolved regardless of whether a country is present, so the identifier is never
 * saved with a null typeId when a valid type was selected. Returns null if empty/not relevant.
 */
export function parseLegalIdentifierString(
  formatted: string | undefined | null,
  allLeiTypes: LegalEntityIdType[],
): { value: string; typeId: string | null; countryCode: string | null; typeName: string | null } | null {
  if (!formatted || formatted === EMPTY_VALUE || formatted === NOT_RELEVANT) return null;

  // Split off an optional trailing "(country)" group (which may be empty, e.g. "()").
  // Only peel it when it is safe to read as a country: an empty "()", a 2-3 letter code, a known
  // country name, or when the remainder still carries a " - typeName" segment (the dialog always
  // emits "(country)" after the type). Otherwise a value that merely ends in parentheses — e.g.
  // "12345 (branch A)" — would be mis-split into value "12345" + bogus country "branch A".
  let rest = formatted.trim();
  let countryCode: string | null = null;
  const countryMatch = rest.match(/^(.*)\s*\(([^)]*)\)$/);
  if (countryMatch) {
    const beforeParens = countryMatch[1].trim();
    const countryRaw = countryMatch[2].trim();
    const looksLikeCode = countryRaw.length <= 3 && countryRaw === countryRaw.toUpperCase();
    const resolvedFromName = Object.entries(COUNTRIES).find(([, c]) => c.name === countryRaw)?.[0];
    const isKnownCountry = looksLikeCode || !!resolvedFromName;
    const remainderHasType = /\s+-\s+/.test(beforeParens);
    if (!countryRaw || isKnownCountry || remainderHasType) {
      rest = beforeParens;
      if (countryRaw) {
        // Resolve country code: a 2-3 letter code as-is, otherwise by name, else keep the raw text.
        countryCode = looksLikeCode ? countryRaw : (resolvedFromName ?? countryRaw);
      }
    }
  }

  // Split the remainder into "value - typeName" on the first " - "; the type is optional.
  const typeMatch = rest.match(/^(.+?)\s+-\s+(.+)$/);
  const value = (typeMatch ? typeMatch[1] : rest).trim();
  const typeName = typeMatch ? typeMatch[2].trim() : null;
  const typeId = typeName ? (allLeiTypes.find((t) => t.name === typeName)?.id ?? null) : null;

  return { value, typeId, countryCode, typeName };
}

/**
 * Builds the payee verification request payload from the current payee rows state.
 * The `columnKey` parameter selects whether to read from 'manualProcess' or 'finalReview'.
 */
export function buildPayeeVerificationRequest(
  rows: ExpandableTableRow[],
  knoxId: string,
  privatePayee: boolean,
  effectiveDate: number | undefined,
  sanctionsStatus: string | null,
  sanctionsScreeningDate: number | undefined,
  allLeiTypes: LegalEntityIdType[],
): StoreSupplierValidationPayeeVerificationRecordRequest {
  const getValue = (dataPointType: DataPointType, columnKey: 'manualProcess' | 'finalReview'): string | null => {
    const row = rows.find((r) => r.dataPointType === dataPointType && !r.isChildRow);
    if (!row) return null;
    const raw = cellContentToString(row[columnKey]);
    if (raw === NOT_RELEVANT) return null;
    if (!raw || raw === EMPTY_VALUE) return '';
    if (dataPointType === 'country' || dataPointType === 'accountCountry') {
      // Convert country name to code
      const codeFromDisplay = raw.match(/^([A-Z]{2,3})\s*-\s*.+$/)?.[1];
      const code =
        codeFromDisplay || (Object.entries(COUNTRIES).find(([c, country]) => country.name === raw || c === raw)?.[0] ?? raw);
      return code || null;
    }
    return raw || null;
  };

  const buildLeiList = (columnKey: 'manualProcess' | 'finalReview'): VerificationLegalIdentifierRequest[] | null => {
    // Include the parent legalIdentifiers row (not a child) and all child legalIdentifiers rows
    const leiRows = rows.filter((r) => r.dataPointType === 'legalIdentifiers');

    const leis: VerificationLegalIdentifierRequest[] = [];
    for (const row of leiRows) {
      const raw = cellContentToString(row[columnKey]);
      // NOT_RELEVANT and empty both contribute no identifier. The empty field is serialized as null
      // (below), not an empty list, matching the read model (transform maps [] -> null) and keeping the
      // two flows consistent — a field marked Not relevant in one flow does not save [] in the other.
      if (!raw || raw === EMPTY_VALUE || raw === NOT_RELEVANT) continue;

      const parsed = parseLegalIdentifierString(raw, allLeiTypes);
      if (parsed) {
        leis.push({ value: parsed.value, typeId: parsed.typeId, countryCode: parsed.countryCode, isPrimary: false });
      }
    }

    // Empty list saves as null, never [] — see the read-model convention above.
    return leis.length ? markFirstAsPrimary(leis) : null;
  };

  const buildForFlowType = (
    columnKey: 'manualProcess' | 'finalReview',
  ): StoreSupplierValidationPayeeVerificationRecordRequest => ({
    name: getValue('name', columnKey),
    additionalName: ((): string | null => {
      const additionalRow = rows.find((r) => r.dataPointType === 'name' && r.isChildRow);
      if (!additionalRow) return null;
      const raw = cellContentToString(additionalRow[columnKey]);
      return raw === NOT_RELEVANT ? null : raw && raw !== EMPTY_VALUE ? raw : '';
    })(),
    address: getValue('address', columnKey),
    additionalAddress: ((): string | null => {
      const additionalRow = rows.find((r) => r.dataPointType === 'address' && r.isChildRow);
      if (!additionalRow) return null;
      const raw = cellContentToString(additionalRow[columnKey]);
      return raw === NOT_RELEVANT ? null : raw && raw !== EMPTY_VALUE ? raw : '';
    })(),
    countryCode: getValue('country', columnKey),
    legalEntityIdentifiers: buildLeiList(columnKey),
    sanctionsScreening:
      sanctionsStatus && sanctionsScreeningDate
        ? { result: sanctionsStatus as SanctionsScreeningResultStatus, screeningTimestamp: sanctionsScreeningDate }
        : null,
    alreadyExistingValidatedPayeeKnoxId: trimToNull(knoxId),
    isPrivate: privatePayee,
    isUpdateRequired: false,
    effectiveTimestamp: effectiveDate ?? null,
  });

  return buildForFlowType('finalReview');
}

/**
 * Builds the payee verification request for the Manual flow (reads from manualProcess column).
 */
export function buildManualPayeeVerificationRequest(
  rows: ExpandableTableRow[],
  knoxId: string,
  privatePayee: boolean,
  effectiveDate: number | undefined,
  sanctionsStatus: string | null,
  sanctionsScreeningDate: number | undefined,
  allLeiTypes: LegalEntityIdType[],
): StoreSupplierValidationPayeeVerificationRecordRequest {
  const getValue = (dataPointType: DataPointType): string | null => {
    const row = rows.find((r) => r.dataPointType === dataPointType && !r.isChildRow);
    if (!row) return null;
    const raw = cellContentToString(row.manualProcess);
    if (raw === NOT_RELEVANT) return null;
    if (!raw || raw === EMPTY_VALUE) return '';
    if (dataPointType === 'country') {
      const codeFromDisplay = raw.match(/^([A-Z]{2,3})\s*-\s*.+$/)?.[1];
      const code =
        codeFromDisplay || (Object.entries(COUNTRIES).find(([c, country]) => country.name === raw || c === raw)?.[0] ?? raw);
      return code || null;
    }
    return raw || null;
  };

  const buildLeiList = (): VerificationLegalIdentifierRequest[] | null => {
    const leiRows = rows.filter((r) => r.dataPointType === 'legalIdentifiers');

    const leis: VerificationLegalIdentifierRequest[] = [];
    for (const row of leiRows) {
      const raw = cellContentToString(row.manualProcess);
      // NOT_RELEVANT and empty both contribute no identifier. The empty field is serialized as null
      // (below), not an empty list, matching the read model (transform maps [] -> null) and keeping the
      // two flows consistent — a field marked Not relevant in one flow does not save [] in the other.
      if (!raw || raw === EMPTY_VALUE || raw === NOT_RELEVANT) continue;

      const parsed = parseLegalIdentifierString(raw, allLeiTypes);
      if (parsed) {
        leis.push({ value: parsed.value, typeId: parsed.typeId, countryCode: parsed.countryCode, isPrimary: false });
      }
    }

    // Empty list saves as null, never [] — see the read-model convention above.
    return leis.length ? markFirstAsPrimary(leis) : null;
  };

  return {
    name: getValue('name'),
    additionalName: ((): string | null => {
      const additionalRow = rows.find((r) => r.dataPointType === 'name' && r.isChildRow);
      if (!additionalRow) return null;
      const raw = cellContentToString(additionalRow.manualProcess);
      return raw === NOT_RELEVANT ? null : raw && raw !== EMPTY_VALUE ? raw : '';
    })(),
    address: getValue('address'),
    additionalAddress: ((): string | null => {
      const additionalRow = rows.find((r) => r.dataPointType === 'address' && r.isChildRow);
      if (!additionalRow) return null;
      const raw = cellContentToString(additionalRow.manualProcess);
      return raw === NOT_RELEVANT ? null : raw && raw !== EMPTY_VALUE ? raw : '';
    })(),
    countryCode: getValue('country'),
    legalEntityIdentifiers: buildLeiList(),
    sanctionsScreening:
      sanctionsStatus && sanctionsScreeningDate
        ? { result: sanctionsStatus as SanctionsScreeningResultStatus, screeningTimestamp: sanctionsScreeningDate }
        : null,
    alreadyExistingValidatedPayeeKnoxId: trimToNull(knoxId),
    isPrivate: privatePayee,
    isUpdateRequired: false,
    effectiveTimestamp: effectiveDate ?? null,
  };
}

/**
 * Builds an account verification request from a single account section's rows.
 */
export function buildAccountVerificationRequest(
  rows: ExpandableTableRow[],
  privateAccount: boolean,
  validationType: string | undefined,
  columnKey: 'manualProcess' | 'finalReview',
  bankCodeOverrideRow?: ExpandableTableRow,
): StoreSupplierValidationAccountVerificationRecordRequest {
  const getValue = (dataPointType: DataPointType): string | null => {
    const row = rows.find((r) => r.dataPointType === dataPointType);
    if (!row) return null;
    const raw = cellContentToString(row[columnKey]);
    if (raw === NOT_RELEVANT) return null;
    if (!raw || raw === EMPTY_VALUE) return '';
    if (dataPointType === 'accountCountry') {
      const codeFromDisplay = raw.match(/^([A-Z]{2,3})\s*-\s*.+$/)?.[1];
      const code =
        codeFromDisplay || (Object.entries(COUNTRIES).find(([c, country]) => country.name === raw || c === raw)?.[0] ?? raw);
      return code || null;
    }
    return raw || null;
  };

  const readBankCode = (): string | null => {
    if (!bankCodeOverrideRow) return getValue('bankCode');
    const raw = cellContentToString(bankCodeOverrideRow[columnKey]);
    if (raw === NOT_RELEVANT) return null;
    if (!raw || raw === EMPTY_VALUE) return '';
    return raw;
  };

  return {
    countryCode: getValue('accountCountry'),
    swift: getValue('swiftCode'),
    bankCode: readBankCode(),
    bankName: getValue('bankName'),
    branchCode: getValue('branchCode'),
    accountNumber: getValue('accountNumber'),
    iban: getValue('iban'),
    furtherCredit: getValue('forFurtherCredit'),
    validationTimestamp: null,
    // An unselected validation type is "no value", which — per the null/empty-string convention —
    // is an empty string, not null (null is reserved for "Not relevant"). '' is not a member of the
    // enum, so it is cast to the field's type.
    type: (validationType || '') as ValidatedPayeeAccountValidationStatusSupplierValidationVerification,
    achDidReceiveAccount: null,
    achDidReceiveEin: null,
    // CLABE (Mexican local account format) persists to localFormat. A CLABE row exists only for MX
    // accounts (see recordHasMexicoAccountCountry in populateAccountDetailsRows), so its presence —
    // not the live Account Country cell — is what gates persistence. This keeps save in lockstep with
    // render: if the row is shown, its value is saved; there is no separate country check that could
    // disagree and silently drop an entered CLABE. NOT_RELEVANT/empty collapse to null (getValue
    // returns null for NOT_RELEVANT and '' for empty; the trailing `|| null` maps '' to null too),
    // matching the legacy form's localFormat convention (blank/irrelevant local format => null).
    localFormat: rows.some((r) => r.dataPointType === 'clabe') ? getValue('clabe') || null : null,
    isPrivate: privateAccount,
  };
}

export type AccountRequestGroup = {
  sectionId: string;
  slot: 'primary' | 'secondary';
  manual: StoreSupplierValidationAccountVerificationRecordRequest;
  final: StoreSupplierValidationAccountVerificationRecordRequest;
};

/**
 * Serializes account sections into store/update request groups. A section with an
 * "Additional Bank Code" child row splits into two groups (primary + secondary) that differ
 * only in bankCode; otherwise it yields a single primary group.
 *
 * The section's dropdown/toggle hold the ACTIVE flow's (possibly edited) metadata, so only that
 * flow's request carries them; the other flow's request carries its OWN stored metadata
 * (section.otherFlowValues). Stamping the section values into both requests let a save that fires
 * both flows — e.g. deleting a row that still carried the other column's value — overwrite the
 * other flow's validation type / private flag with the active flow's.
 */
export function buildAllAccountRequests(sections: AccountSection[], activeFlowType: VerificationFlowType): AccountRequestGroup[] {
  return sections.flatMap((section) => {
    const additionalBankCodeRow = section.rows.find(
      (r) => r.dataPointType === 'bankCode' && r.dataPoint === ADDITIONAL_BANK_CODE_LABEL,
    );

    const sectionMetadata = { validationType: section.validationType, privateAccount: section.privateAccount };
    const otherFlowMetadata = {
      validationType: section.otherFlowValues?.validationType,
      privateAccount: section.otherFlowValues?.privateAccount ?? false,
    };
    const manualMetadata = activeFlowType === VerificationFlowType.Manual ? sectionMetadata : otherFlowMetadata;
    const finalMetadata = activeFlowType === VerificationFlowType.Final ? sectionMetadata : otherFlowMetadata;

    const primary: AccountRequestGroup = {
      sectionId: section.id,
      slot: 'primary',
      manual: buildAccountVerificationRequest(
        section.rows,
        manualMetadata.privateAccount,
        manualMetadata.validationType,
        'manualProcess',
      ),
      final: buildAccountVerificationRequest(
        section.rows,
        finalMetadata.privateAccount,
        finalMetadata.validationType,
        'finalReview',
      ),
    };

    if (!additionalBankCodeRow) {
      return [primary];
    }

    const secondary: AccountRequestGroup = {
      sectionId: section.id,
      slot: 'secondary',
      manual: buildAccountVerificationRequest(
        section.rows,
        manualMetadata.privateAccount,
        manualMetadata.validationType,
        'manualProcess',
        additionalBankCodeRow,
      ),
      final: buildAccountVerificationRequest(
        section.rows,
        finalMetadata.privateAccount,
        finalMetadata.validationType,
        'finalReview',
        additionalBankCodeRow,
      ),
    };

    return [primary, secondary];
  });
}

/**
 * Derives a CellState descriptor for a given row and column key.
 * All icon, background, and content decisions are driven from this descriptor.
 *
 * Rules:
 * - Auto Matching column: Match/PartialMatch → green; Mismatch → red; Unknown/NotRelevant/no data → gray.
 * - Manual Processing column:
 *   - value is the NOT_RELEVANT sentinel (manually marked) → editedManual (orange, Info icon)
 *   - auto NotRelevant with no manual value → notRelevant (gray, ? icon)
 *   - edited in session OR manualChangedFromInitial → editedManual (orange, Info icon)
 *   - else Match → green / Mismatch/noData/needsSearch → yellow (Search icon)
 * - Final Review column:
 *   - Final value is the NOT_RELEVANT sentinel → editedFinal, dark green when changed/edited this
 *     session, else green
 *   - auto NotRelevant with no manual/final value (and Manual not marked with a present Final) → gray
 *   - empty Final but Manual edited → editedManual (orange, inherited)
 *   - ANY non-empty Final value → editedFinal, dark green when finalChangedFromInitial or edited this
 *     session, else green. This overrides auto-match state — a non-empty Final value is never yellow.
 *   - empty Final, otherwise → noData/needsSearch (yellow, Search icon)
 */
export function computeCellState(
  row: ExpandableTableRow,
  colKey: string,
  sessionEdits: Set<string>,
  // Stable per-row key used to look up session edits. Must match the key GenericTable stores under —
  // a stable row id, NOT the positional index, so the "edited" highlight stays on the right row after
  // rows are added/deleted.
  rowKey: number,
): CellState {
  const hasVendor = !isEmptyValue(extractVendorValue(row));
  const hasEvidence = Array.isArray(row.evidence) && row.evidence.length > 0;
  const manualStr = cellContentToString(row.manualProcess);
  const finalStr = cellContentToString(row.finalReview);
  const hasManual = !isEmptyValue(manualStr);
  const hasFinal = !isEmptyValue(finalStr);
  const isNotRelevantValue = manualStr === NOT_RELEVANT;
  const isFinalNotRelevantValue = finalStr === NOT_RELEVANT;
  const manualEditedInSession = sessionEdits.has(`${rowKey}-${COLUMN_KEYS.MANUAL_PROCESS}`);
  const finalEditedInSession = sessionEdits.has(`${rowKey}-${COLUMN_KEYS.FINAL_REVIEW}`);
  const autoMatch = row.automatedMatching;

  // ── Auto Match column ────────────────────────────────────────────────────
  if (colKey === COLUMN_KEYS.AUTOMATED_MATCHING) {
    if (autoMatch === 'Match') {
      return { variant: 'match', autoMatchIcon: 'checkcheck', autoMatchBg: 'green', reviewBg: 'none', reviewIcon: null };
    }
    if (autoMatch === 'PartialMatch') {
      return { variant: 'partialMatch', autoMatchIcon: 'check', autoMatchBg: 'green', reviewBg: 'none', reviewIcon: null };
    }
    if (autoMatch === 'Mismatch') {
      return { variant: 'mismatch', autoMatchIcon: 'x', autoMatchBg: 'red', reviewBg: 'none', reviewIcon: null };
    }
    // 'Unknown' (e.g. a newly-added row not yet matched) and 'NotRelevant' both render a
    // neutral gray '?' here. Only the Auto Matching column treats Unknown this way — the
    // Manual/Final columns keep their actionable "needs work" state so the operator still
    // sees the empty row as something to fill in.
    if (autoMatch === 'NotRelevant' || autoMatch === 'Unknown') {
      return { variant: 'notRelevant', autoMatchIcon: 'question', autoMatchBg: 'gray', reviewBg: 'none', reviewIcon: null };
    }
    if (!hasVendor && !hasEvidence) {
      return { variant: 'noData', autoMatchIcon: 'question', autoMatchBg: 'gray', reviewBg: 'none', reviewIcon: null };
    }
    if (!hasEvidence) {
      return { variant: 'needsSearch', autoMatchIcon: 'dash', autoMatchBg: 'gray', reviewBg: 'none', reviewIcon: null };
    }
    return { variant: 'needsSearch', autoMatchIcon: 'dash', autoMatchBg: 'gray', reviewBg: 'none', reviewIcon: null };
  }

  // ── Manual Process column ────────────────────────────────────────────────
  if (colKey === COLUMN_KEYS.MANUAL_PROCESS) {
    if (isNotRelevantValue) {
      // A Not-relevant sentinel is orange (edited) only when the user actually marked it — edited this
      // session or persisted as changed-from-initial. A Not-relevant that is unchanged from its initial
      // (server) state is not an edit, so it renders gray like the auto-derived not-relevant below.
      if (manualEditedInSession || row.manualChangedFromInitial) {
        return { variant: 'editedManual', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'orange', reviewIcon: 'info' };
      }
      return { variant: 'notRelevant', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'gray', reviewIcon: 'question' };
    }
    if (autoMatch === 'NotRelevant' && !hasManual) {
      // Auto-derived not-relevant with no manual value => gray (unchanged).
      return { variant: 'notRelevant', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'gray', reviewIcon: 'question' };
    }
    // Orange marks a manually-edited value: either edited in the current session, or persisted as
    // changed-from-initial by the server (survives save/reload). Session is checked alongside the
    // persisted flag so a value stays orange from the moment it is edited through every later reload.
    if (manualEditedInSession || row.manualChangedFromInitial) {
      return { variant: 'editedManual', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'orange', reviewIcon: 'info' };
    }
    if (autoMatch === 'Match') {
      return { variant: 'match', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'green', reviewIcon: null };
    }
    if (autoMatch === 'PartialMatch') {
      return { variant: 'partialMatch', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'green', reviewIcon: null };
    }
    if (autoMatch === 'Mismatch') {
      return { variant: 'mismatch', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'yellow', reviewIcon: 'search' };
    }
    if (!hasVendor && !hasEvidence) {
      return { variant: 'noData', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'yellow', reviewIcon: 'search' };
    }
    if (!hasEvidence) {
      return { variant: 'needsSearch', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'yellow', reviewIcon: 'search' };
    }
    return { variant: 'needsSearch', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'yellow', reviewIcon: 'search' };
  }

  // ── Final Review column ──────────────────────────────────────────────────
  if (colKey === COLUMN_KEYS.FINAL_REVIEW) {
    if (
      (isNotRelevantValue && !hasFinal) ||
      isFinalNotRelevantValue ||
      (autoMatch === 'NotRelevant' && !hasManual && !hasFinal)
    ) {
      // A Not-relevant sentinel in Final is a *value* only when the user actually marked it — edited
      // this session or persisted as changed-from-initial — in which case it is darkGreen. A
      // Not-relevant that is unchanged from its initial (server) state is not an edit and stays gray,
      // like the auto-derived not-relevant.
      if (isFinalNotRelevantValue) {
        const dark = finalEditedInSession || row.finalChangedFromInitial;
        if (dark) {
          return {
            variant: 'editedFinal',
            autoMatchIcon: null,
            autoMatchBg: 'none',
            reviewBg: 'darkGreen',
            reviewIcon: null,
          };
        }
        return { variant: 'notRelevant', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'gray', reviewIcon: 'question' };
      }
      return { variant: 'notRelevant', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'gray', reviewIcon: 'question' };
    }
    // Final Review inherits the Manual value's "edited" orange indication when Manual was edited
    // (this session or persisted as changed-from-initial) AND Final has no value of its own —
    // mirroring the Manual Processing column. Once Final has a value it shows its own state below.
    if (!hasFinal && (manualEditedInSession || row.manualChangedFromInitial)) {
      return { variant: 'editedManual', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'orange', reviewIcon: 'info' };
    }
    // Any non-empty Final value is green; dark green when changed-from-initial or edited this
    // session. This overrides auto-match state — a Final value is never yellow.
    if (hasFinal) {
      const dark = finalEditedInSession || row.finalChangedFromInitial;
      return {
        variant: 'editedFinal',
        autoMatchIcon: null,
        autoMatchBg: 'none',
        reviewBg: dark ? 'darkGreen' : 'green',
        reviewIcon: null,
      };
    }
    if (!hasVendor && !hasEvidence) {
      return { variant: 'noData', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'yellow', reviewIcon: 'search' };
    }
    return { variant: 'needsSearch', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'yellow', reviewIcon: 'search' };
  }

  return { variant: 'empty', autoMatchIcon: null, autoMatchBg: 'none', reviewBg: 'none', reviewIcon: null };
}

/**
 * Returns the background CSS colour for a table cell based on its CellState.
 */
export function getCellBgColor(state: CellState, colKey: string): string | undefined {
  if (colKey === COLUMN_KEYS.AUTOMATED_MATCHING) {
    const bg = CELL_BG_COLORS[state.autoMatchBg];
    return bg !== 'transparent' ? bg : undefined;
  }
  if (colKey === COLUMN_KEYS.MANUAL_PROCESS || colKey === COLUMN_KEYS.FINAL_REVIEW) {
    const bg = CELL_BG_COLORS[state.reviewBg];
    return bg !== 'transparent' ? bg : undefined;
  }
  return undefined;
}

/**
 * Counts the number of child rows immediately following `startIndex` that share
 * the same `dataPointType`.
 */
export function getChildRowCount(rows: ExpandableTableRow[], startIndex: number, dataPointType: DataPointType): number {
  let count = 0;
  for (let i = startIndex + 1; i < rows.length; i++) {
    if (rows[i].isChildRow && rows[i].dataPointType === dataPointType) {
      count++;
    } else {
      break;
    }
  }
  return count;
}

/**
 * Whether a parent row can have more child rows added to it.
 */
export function canAddChildRow(rows: ExpandableTableRow[], rowIndex: number, dataPointType: DataPointType): boolean {
  const limit = DATA_POINT_ADD_LIMITS[dataPointType] ?? Infinity;
  return getChildRowCount(rows, rowIndex, dataPointType) < limit;
}

/**
 * Resolves the display value for a review cell.
 * For Final Review, falls back to the Manual Processing value when Final Review is empty.
 */
export function resolveReviewDisplayValue(row: ExpandableTableRow, colKey: string): React.ReactNode {
  const cellContent = row[colKey as keyof ExpandableTableRow] as React.ReactNode;
  if (colKey === COLUMN_KEYS.FINAL_REVIEW) {
    if (isEmptyValue(cellContentToString(cellContent)) && !isEmptyValue(cellContentToString(row.manualProcess))) {
      return row.manualProcess;
    }
  }
  return cellContent;
}

/**
 * Resolves the text to render inside a review cell (Manual Processing or Final Review).
 * Handles "Not relevant", empty values, and country-code-to-name conversion.
 */
export function resolveReviewCellText(row: ExpandableTableRow, colKey: string, state: CellState): React.ReactNode {
  // Show the "Not relevant" text both for the auto-derived not-relevant state (gray) and for a cell
  // whose value is the NOT_RELEVANT sentinel because the user checked the "Not relevant" box — the
  // latter is colored editedManual (orange) / editedFinal (green), not the notRelevant variant, so it
  // must be matched on the cell value here or the cell would render colored but with no text.
  if (
    state.variant === 'notRelevant' ||
    cellContentToString(row[colKey as keyof ExpandableTableRow] as React.ReactNode) === NOT_RELEVANT
  ) {
    return NOT_RELEVANT;
  }

  const displayValue = resolveReviewDisplayValue(row, colKey);

  if (displayValue === undefined || isEmptyValue(cellContentToString(displayValue))) {
    return '';
  }

  if (row.dataPointType === 'country' || row.dataPointType === 'accountCountry') {
    const cellValue = cellContentToString(displayValue);
    if (cellValue && cellValue.length <= 3 && cellValue === cellValue.toUpperCase()) {
      return formatCountryDisplay(cellValue);
    }
    return cellValue;
  }

  return displayValue;
}

export interface TopSectionValues {
  knoxId: string | null;
  effectiveDate: number | undefined;
  privatePayee: boolean;
  sanctionsStatus: string | null;
  sanctionsScreeningDate: number | undefined;
}

function flowPayees(
  verificationsAndAutoMatch: VerificationsAndAutoMatch,
  activeFlowType: VerificationFlowType,
): {
  activePayee: PayeeWithAccountsVerificationV2['payee'];
  otherPayee: PayeeWithAccountsVerificationV2['payee'];
} {
  const manualPayee = verificationsAndAutoMatch.manualVerification.payee;
  const finalPayee = verificationsAndAutoMatch.finalVerification.payee;
  return activeFlowType === VerificationFlowType.Final
    ? { activePayee: finalPayee, otherPayee: manualPayee }
    : { activePayee: manualPayee, otherPayee: finalPayee };
}

/**
 * Derives the top-section initial values from the active flow's payee, falling back to the OTHER
 * flow's payee (prefill — e.g. an Approver opening a record whose Final payee doesn't exist yet),
 * and finally to the evidence/verificationData tail. The fallback is at the PAYEE level, not
 * per-field: the request builders write every one of these fields unconditionally on save, so an
 * existing flow payee is a complete snapshot — falling back per-field would resurrect a value the
 * user deliberately cleared. Knox ID keeps the "already existing payee only, never record.knoxId"
 * rule (a completed record carries the newly-created Knox ID, which must not prefill on reopen).
 */
export function deriveTopSectionInitialValues(
  verificationsAndAutoMatch: VerificationsAndAutoMatch,
  verificationData: SupplierValidationPayeeWithAccountsVerification,
  evidence: SupplierValidationVerificationRecordEvidence | undefined,
  activeFlowType: VerificationFlowType,
): TopSectionValues {
  const { activePayee, otherPayee } = flowPayees(verificationsAndAutoMatch, activeFlowType);
  const sourcePayee = activePayee ?? otherPayee;

  if (sourcePayee) {
    return {
      knoxId: sourcePayee.alreadyExistingValidatedPayee?.knoxId ?? null,
      effectiveDate: sourcePayee.effectiveTimestamp ?? undefined,
      privatePayee: sourcePayee.isPrivate,
      sanctionsStatus: (sourcePayee.sanctionsScreening?.result as SanctionsScreeningResultStatus | null) ?? null,
      sanctionsScreeningDate: sourcePayee.sanctionsScreening?.screeningTimestamp ?? undefined,
    };
  }

  const dataPayee = verificationData?.payee;
  return {
    knoxId: dataPayee?.alreadyExistingValidatedPayee?.knoxId ?? null,
    effectiveDate: evidence?.effectiveDate || dataPayee?.effectiveTimestamp || undefined,
    privatePayee: dataPayee?.isPrivate ?? false,
    sanctionsStatus: (dataPayee?.sanctionsScreening?.result as SanctionsScreeningResultStatus | null) ?? null,
    sanctionsScreeningDate: dataPayee?.sanctionsScreening?.screeningTimestamp ?? undefined,
  };
}

export interface TopSectionFlowDiffs {
  knoxId?: string;
  effectiveDate?: string;
  privatePayee?: string;
  sanctionsStatus?: string;
  sanctionsScreeningDate?: string;
}

/** Toggle values read as On/Off in the hint, matching how a toggle presents them. */
const toggleText = (value: boolean): string => (value ? 'On' : 'Off');

// Must mirror the Sanctions radio labels in TopSection.tsx so the hint names the value exactly as
// the control shows it (getTextFromSanctionStatus says 'Potential matches', the radio 'Potential Matches').
const SANCTIONS_STATUS_TEXT: Record<string, string> = {
  [SanctionsScreeningResultStatus.Passed]: 'Screened',
  [SanctionsScreeningResultStatus.Rejected]: 'Potential Matches',
};

/** The OTHER flow's raw saved values. Raw (not formatted) so the hint strings can be recomputed
 *  against the LIVE field state on every render — a stored, pre-formatted diff goes stale the
 *  moment the user reconciles the field, because nothing refetches verificationsAndAutoMatch
 *  after an in-tab save. */
export interface OtherFlowPayeeValues {
  knoxId?: string;
  effectiveDate?: number;
  privatePayee?: boolean;
  sanctionsStatus?: string;
  sanctionsScreeningDate?: number;
}

export function getOtherFlowPayeeValues(
  verificationsAndAutoMatch: VerificationsAndAutoMatch,
  activeFlowType: VerificationFlowType,
): OtherFlowPayeeValues {
  const { otherPayee } = flowPayees(verificationsAndAutoMatch, activeFlowType);
  if (!otherPayee) {
    return {};
  }

  const values: OtherFlowPayeeValues = {};

  if (otherPayee.alreadyExistingValidatedPayee?.knoxId) {
    values.knoxId = otherPayee.alreadyExistingValidatedPayee.knoxId;
  }
  if (otherPayee.effectiveTimestamp) {
    values.effectiveDate = otherPayee.effectiveTimestamp;
  }
  values.privatePayee = otherPayee.isPrivate;
  if (otherPayee.sanctionsScreening?.result) {
    values.sanctionsStatus = otherPayee.sanctionsScreening.result;
  }
  if (otherPayee.sanctionsScreening?.screeningTimestamp) {
    values.sanctionsScreeningDate = otherPayee.sanctionsScreening.screeningTimestamp;
  }

  return values;
}

export interface TopSectionLiveValues {
  knoxId: string | null | undefined;
  effectiveDate: number | undefined;
  privatePayee: boolean;
  sanctionsStatus: string | null;
  sanctionsScreeningDate: number | undefined;
}

/**
 * Formats the hint shown under each top-section field: the other flow's saved value, only while it
 * differs from the CURRENT live value — typing/picking the matching value clears the hint
 * immediately, no save or refetch needed. No hint when the other side is the empty one: there
 * would be no value to show. Dates compare on the formatted day (the inputs normalize time-of-day
 * differently than stored timestamps).
 */
export function computeTopSectionFlowDiffs(otherValues: OtherFlowPayeeValues, live: TopSectionLiveValues): TopSectionFlowDiffs {
  const diffs: TopSectionFlowDiffs = {};

  if (otherValues.knoxId && otherValues.knoxId !== (live.knoxId ?? '').trim()) {
    diffs.knoxId = otherValues.knoxId;
  }
  if (otherValues.effectiveDate && formatDate(otherValues.effectiveDate) !== formatDate(live.effectiveDate)) {
    diffs.effectiveDate = formatDate(otherValues.effectiveDate);
  }
  if (otherValues.privatePayee !== undefined && otherValues.privatePayee !== live.privatePayee) {
    diffs.privatePayee = toggleText(otherValues.privatePayee);
  }
  if (otherValues.sanctionsStatus && otherValues.sanctionsStatus !== live.sanctionsStatus) {
    diffs.sanctionsStatus = SANCTIONS_STATUS_TEXT[otherValues.sanctionsStatus] ?? otherValues.sanctionsStatus;
  }
  if (
    otherValues.sanctionsScreeningDate &&
    formatDate(otherValues.sanctionsScreeningDate) !== formatDate(live.sanctionsScreeningDate)
  ) {
    diffs.sanctionsScreeningDate = formatDate(otherValues.sanctionsScreeningDate);
  }

  return diffs;
}

export interface PayeeTopSectionRequestValues {
  knoxId: string;
  privatePayee: boolean;
  effectiveDate: number | undefined;
  sanctionsStatus: string | null;
  sanctionsScreeningDate: number | undefined;
}

/**
 * Routes the top-section values per flow for the payee save: the ACTIVE flow's request carries the
 * live (possibly edited) state, while the other flow's request round-trips that flow's own stored
 * values. Both payee requests can fire in one save — e.g. a pure Approver editing a Manual
 * Processing cell (canEditManualProcess is true for approvers) alongside a top-section change —
 * and feeding the single live state into both stamped the active flow's Knox ID / Private Payee /
 * Sanctions / Effective Date over the other flow's record. Mirrors buildAllAccountRequests'
 * per-flow metadata routing.
 */
export function routePayeeTopSectionValues(
  activeFlowType: VerificationFlowType,
  liveValues: TopSectionLiveValues,
  otherFlowValues: OtherFlowPayeeValues,
): { manual: PayeeTopSectionRequestValues; final: PayeeTopSectionRequestValues } {
  const activeFlowRequestValues: PayeeTopSectionRequestValues = {
    knoxId: liveValues.knoxId ?? '',
    privatePayee: liveValues.privatePayee,
    effectiveDate: liveValues.effectiveDate,
    sanctionsStatus: liveValues.sanctionsStatus,
    sanctionsScreeningDate: liveValues.sanctionsScreeningDate,
  };
  const otherFlowRequestValues: PayeeTopSectionRequestValues = {
    knoxId: otherFlowValues.knoxId ?? '',
    privatePayee: otherFlowValues.privatePayee ?? false,
    effectiveDate: otherFlowValues.effectiveDate,
    sanctionsStatus: otherFlowValues.sanctionsStatus ?? null,
    sanctionsScreeningDate: otherFlowValues.sanctionsScreeningDate,
  };

  return activeFlowType === VerificationFlowType.Manual
    ? { manual: activeFlowRequestValues, final: otherFlowRequestValues }
    : { manual: otherFlowRequestValues, final: activeFlowRequestValues };
}

export interface AccountFlowValues {
  validationType: string | undefined;
  privateAccount: boolean;
}

export interface AccountFlowDiffs {
  validationType?: string;
  privateAccount?: string;
}

/**
 * The section's dropdown/toggle show ONLY the active flow's own stored metadata. There is
 * deliberately no cross-flow fallback: when the active flow has no account record yet, the
 * controls stay empty/off and the other flow's values appear as red hints — displaying them in
 * the controls read as the active flow's own choice and got persisted into it on the next save,
 * silently overriding the flow separation the red hints exist to surface. (No verificationData
 * tail either: the v1 GET behind it defaults to flowType=Final, so it is not flow-neutral.)
 */
export function deriveAccountFlowValues(activeAccount: AccountVerificationRecordV2 | undefined): AccountFlowValues {
  return {
    // || (not ??): an unset type is persisted as '' (see buildAccountVerificationRequest), which
    // is not a dropdown option and must never reach NakedDropdown.
    validationType: activeAccount ? activeAccount.type || undefined : undefined,
    privateAccount: activeAccount?.isPrivate ?? false,
  };
}

/** The OTHER flow's raw account values — captured on the section at build time; the hint itself is
 *  recomputed per render against the section's CURRENT values (computeAccountFlowDiffs) so it can
 *  never go stale. */
export interface OtherFlowAccountValues {
  validationType?: string;
  privateAccount?: boolean;
}

export function getOtherFlowAccountValues(otherAccount: AccountVerificationRecordV2 | undefined): OtherFlowAccountValues {
  if (!otherAccount) {
    return {};
  }
  return {
    validationType: otherAccount.type || undefined,
    privateAccount: otherAccount.isPrivate,
  };
}

/** Hint strings for one account section, comparing the other flow's saved values against the
 *  section's CURRENT values — switching the dropdown/toggle to the matching value clears the hint
 *  immediately. */
export function computeAccountFlowDiffs(
  otherValues: OtherFlowAccountValues,
  live: { validationType: string | undefined; privateAccount: boolean },
): AccountFlowDiffs {
  const diffs: AccountFlowDiffs = {};

  if (otherValues.validationType && otherValues.validationType !== (live.validationType || undefined)) {
    diffs.validationType = convertValidatedAccountValidationSupplierValidationLevelToText(
      otherValues.validationType as ValidatedPayeeAccountValidationStatusSupplierValidationVerification,
    );
  }
  if (otherValues.privateAccount !== undefined && otherValues.privateAccount !== live.privateAccount) {
    diffs.privateAccount = toggleText(otherValues.privateAccount);
  }

  return diffs;
}
